import flatpickr from "flatpickr"
import { DateTime } from "luxon"
import { marked } from "marked"
import DOMPurify from "dompurify"
import tippy from "tippy.js"
import "tippy.js/dist/tippy.css"
import * as util from "./util"
import emailSpellChecker from "@zootools/email-spell-checker"

/* To locally debug this script via Webflow the best option I've found is to
add two scripts in Webflow:
<script src="http://localhost:3000/????.bundle.js"></script>
<script src="angry-boyd-1669aa.netlify.app/????.bundle.js" crossorigin="anonymous"></script>
The below check ensures that if localhost loads successfully the second
(production) script won't run.
Initially I tried to add the script dynamically in Webflow but that didn't
work - I needed it to run immediately but it wouldn't. */
if (typeof SharesyWebflow === "undefined") {
  util.initSentry()

  window.amplitudeOnLoad = function (instance) {
    let eventProperties = {
      Slug: window.location.pathname.split("/").pop(),
      Space: document.querySelector(".space-hidden").innerText.replace("'", ""),
      Venue: document.querySelector(".venue-hidden").innerText.replace("'", ""),
      "Venue + Space": document
        .querySelector(".name-hidden")
        .innerText.replace("'", ""),
      Capacity: document.querySelector(".capacity-hidden").innerText,
      Price: document.querySelector(".price-hidden > .price-inner").innerText,
    }
    util.amplitudeInit(instance, "Spaces Page Viewed", eventProperties)

    // Add the Amplitude Device ID as a query param for cross-domain tracking
    let deviceId
    try {
      deviceId = instance.options.deviceId
    } catch (err) {}
    if (deviceId) {
      try {
        // This is needed when user chooses date+time at search - deviceId isn't known
        // until after findApptTypes runs.
        if (
          document.getElementsByClassName("enquiryonly-hidden")[0].innerText ===
          "true"
        ) {
          let enquiryButton = document.getElementById("enquiry-only-button")
          util.addUrlParamToLink(enquiryButton, "deviceid", deviceId)
        }
      } catch (err) {
        console.error(err)
      }
    }
  }

  var youtubeId

  window.onYouTubeIframeAPIReady = function () {
    let youtubePlayer = new YT.Player("youtube-player", {
      videoId: youtubeId,
      playerVars: {
        playsinline: 0,
        rel: 0,
        modestbranding: 1,
        enablejsapi: 1,
        origin: window.location.href,
        host: "https://www.youtube.com",
      },
      events: {
        onStateChange: youtubeOnPlayerStateChange,
      },
    })
  }

  window.youtubeOnPlayerStateChange = function (event) {
    if (event.data === YT.PlayerState.ENDED) {
      SharesyWebflow.logAmplitudeEvent("Venue viewing video ended")
      youtubeDisplayQuestionnaire()
    } else if (event.data === YT.PlayerState.PLAYING) {
      SharesyWebflow.logAmplitudeEvent("Venue viewing video playing")
    } else if (event.data === YT.PlayerState.PAUSED) {
      SharesyWebflow.logAmplitudeEvent("Venue viewing video paused")
      youtubeDisplayQuestionnaire()
    } /* else if (event.data === YT.PlayerState.BUFFERING) {
    } else if (event.data === YT.PlayerState.CUED) {
    } else if (event.data === -1) { // unstarted
    }*/
  }

  // Create a closure to ensure it only executes once
  var youtubeDisplayQuestionnaire = (function () {
    let executed = false
    return function () {
      if (!executed) {
        executed = true
        document.getElementById("survey-question-1").classList.remove("hide")
        SharesyWebflow.logAmplitudeEvent("Display Venue Viewing Survey")
      }
    }
  })()

  // Wrap everything with Revealing Module Pattern
  window.SharesyWebflow = (function () {
    var spacesJson

    var slug = window.location.pathname.replace("/s/", "")
    var searchParams = util.getSearchParams()

    var fp // flatpickr
    var date
    var availableTimes
    var startTime
    var startTimeOnNextDay = false
    var endTime
    var endTimeOnNextDay = false
    const TIME_NONE = 0
    const TIME_START = 1
    var timeStatus = TIME_NONE
    var dateTextShort

    var checkoutMode = "booking"
    var availableAddons = []
    var selectedAddons = []
    var checkoutNumHours
    var checkoutTotalPrice
    // Used for the checkout option to extend by 30 mins
    var priceIncludingExtra30Mins
    var priceOfExtra30Mins
    // Standard checkout questions (excluding venue-specific questions)
    var standardCheckoutQuestionIds

    var swiper

    var enquiryOnly = false

    var cancelTippy
    var intlTelInputValue
    var cancellationMessage = ""
    var payLaterMessage = ""
    var allowPayLater = false

    function isValidEmail(email) {
      // Copied from public domain code at https://github.com/manishsaraan/email-validator/blob/master/index.js
      const tester =
        /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/
      // Thanks to:
      // http://fightingforalostcause.net/misc/2006/compare-email-regex.php
      // http://thedailywtf.com/Articles/Validating_Email_Addresses.aspx
      // http://stackoverflow.com/questions/201323/what-is-the-best-regular-expression-for-validating-email-addresses/201378#201378
      // https://en.wikipedia.org/wiki/Email_address  The format of an email address is local-part@domain, where the
      // local part may be up to 64 octets long and the domain may have a maximum of 255 octets.[4]
      if (!email) return false

      var emailParts = email.split("@")

      if (emailParts.length !== 2) return false

      var account = emailParts[0]
      var domain = emailParts[1]

      if (account.length > 64) return false
      else if (domain.length > 255) return false

      var domainParts = domain.split(".")

      const tld = domainParts[domainParts.length - 1]
      if (tld.length < 2) return false
      if (tld === "con") return false

      if (
        domainParts.some(function (part) {
          return part.length > 63
        })
      )
        return false

      return tester.test(email)
    }

    function logAmplitudeEvent(eventName, eventProps) {
      let eventProperties = {}
      if (eventProps) {
        eventProperties = eventProps
      }
      try {
        eventProperties["Slug"] = slug
        eventProperties["Space"] = document
          .querySelector(".space-hidden")
          .innerText.replace("'", "")
        eventProperties["Venue"] = document
          .querySelector(".venue-hidden")
          .innerText.replace("'", "")
        eventProperties["Venue + Space"] = document
          .querySelector(".name-hidden")
          .innerText.replace("'", "")
        eventProperties["Capacity"] =
          document.querySelector(".capacity-hidden").innerText
        eventProperties["Price"] = document.querySelector(
          ".price-hidden > .price-inner"
        ).innerText
        let offerMessage = document.getElementById("offer-pill").innerText
        if (offerMessage) {
          eventProperties["Offer Message"] = offerMessage
        }
      } catch (err) {}
      util.logAmplitudeEvent(eventName, eventProperties)
    }

    // Fired each time a date is selected in the date picker
    function flatpickrDateChange(
      selectedDates,
      dateStr,
      instance,
      userGenerated = true
    ) {
      if (selectedDates.length > 0) {
        dateTextShort = selectedDates[0].toLocaleDateString("en-GB", {
          month: "short",
          day: "numeric",
        })
        // Need to remove timezone offset before splitting ISO string
        // See: https://stackoverflow.com/a/50130338
        let d = new Date(
          selectedDates[0].getTime() -
            selectedDates[0].getTimezoneOffset() * 60000
        )
        date = d.toISOString().split("T")[0]

        setDateText()
        try {
          setFromPrice(
            spacesJson[0].dates.filter((d) => d.start_date === date)[0]
              .min_price_per_hour_day,
            true
          )
        } catch (err) {}
        if (userGenerated) {
          try {
            const eventProperties = {
              "Selected Date": date,
            }
            logAmplitudeEvent("Select Date (Space)", eventProperties)
          } catch (err) {
            util.logToSentry(err)
          }
        }
        setTimeAvailability()

        if (startTime && endTime) {
          onTimesChange()
        }
      }
    }

    function updateCancelBy() {
      if (startTime && endTime) {
        document.getElementById("superbooker-get-started").classList.add("hide")
        document
          .getElementById("superbooker-availability-form")
          .classList.remove("hide")
        // If the user has in the past brought up the recurring modal before selecting
        // a date and time, then reveal the recurring button as soon as a date and
        // time is selected.
        if (
          localStorage.getItem("recurringModalShownWhenTimeNotSelected") ===
          "true"
        ) {
          document.getElementById("recurring-button").classList.remove("hide")
          document.getElementById("recurring-link").classList.add("hide")
        }
      } else {
        document
          .getElementById("superbooker-get-started")
          .classList.remove("hide")
        document
          .getElementById("superbooker-availability-form")
          .classList.add("hide")
      }
      if (
        document.getElementById("custom-cancellation-policy").innerText ===
        "true"
      ) {
        return
      }
      let eventDate = DateTime.fromISO(date + "T00:00", { zone: util.TIMEZONE })
      if (startTime) {
        eventDate = DateTime.fromISO(date + "T" + startTime, {
          zone: util.TIMEZONE,
        })
      }
      let eventCancelBy = null
      let purchaseCancelBy = DateTime.now().plus({ days: 2 })
      if (eventDate.minus({ days: 14 }) > DateTime.now()) {
        eventCancelBy = eventDate.minus({ days: 14 })
      }
      let cancellationPolicyText
      let remainingCancellationMessage
      allowPayLater = false
      if (eventCancelBy && eventCancelBy.ts >= purchaseCancelBy.ts) {
        let cancelBy = eventCancelBy.toFormat("ccc dd MMM")
        if (startTime) {
          cancelBy = eventCancelBy.toFormat("t 'on' ccc dd MMM")
        }
        if (eventCancelBy.year > DateTime.now().year) {
          cancelBy += " " + eventCancelBy.toFormat("yyyy")
        }
        cancellationPolicyText =
          "Cancel by " +
          cancelBy +
          " to get a full refund. There is no refund if you cancel after that time."
        remainingCancellationMessage = " until " + cancelBy
        // No point to give a 'pay later' option unless there is at least 5
        // days before they have to pay.
        if (eventCancelBy.ts >= DateTime.now().plus({ days: 7 }).ts) {
          let payLaterDate = eventCancelBy
            .minus({ days: 3 })
            .toFormat("ccc dd MMM")
          payLaterMessage =
            "You won't be charged today - we will take payment on " +
            payLaterDate +
            ". You can cancel for free until " +
            cancelBy +
            "."
          // Use try block because these elements weren't accessible in some
          // browsers after deploying.
          try {
            document.getElementById("venue-visit-secondary").innerText =
              "We can arrange a viewing after you confirm your booking. If you change your mind you can cancel for free until " +
              cancelBy +
              "."
            document.getElementById("payment-option-secondary").innerText =
              "You can pay now, or pay later for no additional fee. If you choose to pay later we'll collect your card details in the next step. Your card will be automatically charged on " +
              payLaterDate +
              ", and you'll get a reminder 24 hours before. Whether you pay now or later, you can cancel for free until " +
              cancelBy +
              "."
            document.getElementById("pay-later-radio-label").innerText =
              "Pay later (" + payLaterDate + ")"
            document.getElementById("booking-option-pay-later").innerText =
              "We will collect your card details in the next step, but you won't be charged until " +
              payLaterDate +
              "."
            document.getElementById("booking-option-cancellation").innerText =
              cancellationPolicyText

            // Only allow pay later if there are no errors above
            allowPayLater = true
          } catch (err) {
            util.logToSentry(err)
          }
        }
      } else {
        cancellationPolicyText =
          "Cancel up to 48 hours after booking to get a full refund. There is no refund if you cancel after that time."
        remainingCancellationMessage = " for 48 hours after booking"
      }
      document.getElementById("remaining-cancellation-message").innerText =
        remainingCancellationMessage
      // Save the message to show on the Stripe Checkout
      cancellationMessage =
        "Free cancellation" + remainingCancellationMessage + "."
      document.getElementById("cancellation-policy-with-date").innerText =
        cancellationPolicyText
      cancelTippy.setContent(cancellationPolicyText)
      document
        .getElementById("checkout-cancel-section")
        .classList.remove("hide")
      document
        .getElementById("cancellation-policy-no-date")
        .classList.add("hide")
      document
        .getElementById("cancellation-policy-with-date")
        .classList.remove("hide")
    }

    function setDateText() {
      let inputTextDate = document.getElementById("input-text-date")
      inputTextDate.innerText = new Date(date).toLocaleDateString("en-GB", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      })
      inputTextDate.classList.add("completed")
      util.updateUrlParam("date", date, false, true)
      setDateTimeText()
    }

    function setDateTimeText() {
      let mobilePriceBlockDate = document.getElementById("price-block-date")
      if (startTime) {
        let timeText = startTime + "-" + endTime
        mobilePriceBlockDate.innerText = dateTextShort + " • " + timeText
      } else {
        mobilePriceBlockDate.innerText = dateTextShort
      }
      updateCancelBy()
    }

    function setTimeAvailability() {
      // Diable all times by default
      timeClear(true)
      const timeButtons = document.getElementsByClassName("time-button")
      for (const tb of timeButtons) {
        tb.removeEventListener("click", timeButtonClick)
        tb.classList.add("no-avail")
      }

      let validTimeButtons = new Array()
      let noAvailStartTimeButtons = new Array()

      // Loop over times for the date
      availableTimes = spacesJson[0].dates.filter(
        (d) => d.start_date === date
      )[0].times
      for (const t of availableTimes) {
        // Loop over time buttons
        for (const tb of timeButtons) {
          let btnTime = DateTime.fromISO(date + "T" + tb.innerText, {
            zone: util.TIMEZONE,
          })
          if (tb.dataset.addDay === "1") {
            btnTime = btnTime.plus({ days: 1 })
          }
          if (btnTime.ts >= t.start_time_utc && btnTime.ts <= t.end_time_utc) {
            tb.classList.remove("no-avail")
            if (btnTime.plus({ hours: t.min_at_hours }).ts > t.end_time_utc) {
              // Time is enabled but can't be selected as a start time
              noAvailStartTimeButtons.push(tb)
            } else {
              // Time is fully enabled
              validTimeButtons.push(tb)
            }
          }
        }
      }
      for (const tb of noAvailStartTimeButtons) {
        // Could have used another reason to explain e.g. 'Available but not a valid start time'
        util.addButtonClassAndTooltip(
          tb,
          "no-avail-start-time",
          "Choose an earlier start time"
        )
      }
      for (const tb of validTimeButtons) {
        util.removeButtonClassAndTooltip(tb, "no-avail-start-time")
        tb.addEventListener("click", timeButtonClick)
      }
    }

    function timeButtonClick(e) {
      let selectedButtons = document.getElementsByClassName(
        "time-button selected"
      )
      if (timeStatus === TIME_NONE) {
        timeStatus = TIME_START
        e.currentTarget.classList.add("selected")
        startTime = selectedButtons[0].innerText
        if (selectedButtons[0].dataset.addDay === "1") {
          startTimeOnNextDay = true
        } else {
          startTimeOnNextDay = false
        }

        document.getElementById("time-modal-header").innerText =
          "Choose an end time"
        document.getElementById("time-modal-clear").style.display = "block"

        document
          .getElementById("start-time-select")
          .classList.remove("highlighted")
        document.getElementById("end-time-select").classList.add("highlighted")
        let inputTextStartTime = document.getElementById(
          "input-text-start-time"
        )
        inputTextStartTime.innerText = startTime
        inputTextStartTime.classList.add("completed")
        // In case an end time was already selected
        let inputTextEndTime = document.getElementById("input-text-end-time")
        inputTextEndTime.innerText = "Choose time"
        inputTextEndTime.classList.remove("completed")

        let startBtnTime = DateTime.fromISO(date + "T" + startTime, {
          zone: util.TIMEZONE,
        })
        if (startTimeOnNextDay) {
          startBtnTime = startBtnTime.plus({ days: 1 })
        }
        let validTimes = availableTimes.filter(
          (t) =>
            startBtnTime.ts >= t.start_time_utc &&
            startBtnTime.plus({ hours: t.min_at_hours }).ts <= t.end_time_utc
        )

        let validTimeButtons = new Array()
        let noAvailEndTimeButtons = new Array()
        const timeButtons = document.getElementsByClassName("time-button")
        for (const tb of timeButtons) {
          util.removeButtonClassAndTooltip(tb, "no-avail-start-time")
          if (
            (startTimeOnNextDay && tb.dataset.addDay !== "1") ||
            (startTimeOnNextDay &&
              tb.dataset.addDay === "1" &&
              tb.innerText < startTime) ||
            (!startTimeOnNextDay &&
              tb.dataset.addDay !== "1" &&
              tb.innerText < startTime)
          ) {
            tb.classList.add("disabled")
          } else if (
            tb.innerText !== startTime &&
            !tb.classList.contains("no-avail")
          ) {
            let btnTime = DateTime.fromISO(date + "T" + tb.innerText, {
              zone: util.TIMEZONE,
            })
            if (tb.dataset.addDay === "1") {
              btnTime = btnTime.plus({ days: 1 })
            }
            for (const l of validTimes) {
              /* Only enable the end times that:
                    - Are at least selected start time + min appt type length
                    - Are no higher than (selected start time + max appt type
                      length) or end time range. Note: enquiry-only spaces only
                      need the minimum time-slot set up e.g. 3 hr, not all
                      time-slots e.g. 3, 4, ... 13 hrs.
              */
              let endTimeOrMaxDuration = l.end_time_utc
              if (l.max_at_hours) {
                endTimeOrMaxDuration = Math.min(
                  startBtnTime.plus({ hours: l.max_at_hours }).ts,
                  l.end_time_utc
                )
              }
              if (
                btnTime.ts >= startBtnTime.plus({ hours: l.min_at_hours }).ts &&
                (btnTime.ts <= endTimeOrMaxDuration ||
                  (enquiryOnly && btnTime.ts <= l.end_time_utc))
              ) {
                validTimeButtons.push(tb)
              } else {
                noAvailEndTimeButtons.push(tb)
              }
            }
          }
        }

        for (const tb of noAvailEndTimeButtons) {
          let btnTime = DateTime.fromISO(date + "T" + tb.innerText, {
            zone: util.TIMEZONE,
          })
          if (tb.dataset.addDay === "1") {
            btnTime = btnTime.plus({ days: 1 })
          }
          let hoursDiff = btnTime.diff(startBtnTime, "hours").toObject().hours
          let minHours = Math.min.apply(
            Math,
            validTimes.map(function (o) {
              return o.min_at_hours
            })
          )
          let reason = ""
          if (hoursDiff < minHours) {
            reason =
              "The minimum duration is " +
              util.roundToMaxDp(minHours, 1) +
              " hours"
          }
          util.addButtonClassAndTooltip(tb, "no-avail-end-time", reason)
        }
        for (const tb of validTimeButtons) {
          util.removeButtonClassAndTooltip(tb, "no-avail-end-time")
          tb.addEventListener("click", timeButtonClick)
          tb.addEventListener("mouseover", mouseoverSelectOtherTimes)
          tb.addEventListener("mouseout", mouseoutSelectOtherTimes)
        }

        document.querySelector(".time-modal").style.display = "none"
        setTimeout(function () {
          document.querySelector(".time-modal").style.display = "flex"
        }, 300)

        try {
          const eventProperties = {
            "Selected Date": document.querySelector(".text-date-input-space")
              .value,
            "Start Time": startTime,
          }
          if (startTimeOnNextDay) {
            eventProperties["Start Time On Next Day"] = true
          }
          logAmplitudeEvent("Select Start Time (Space)", eventProperties)
        } catch (err) {}
      } else if (timeStatus === TIME_START) {
        let tempEndTime = e.currentTarget.innerText
        let tempEndTimeOnNextDay = false
        if (e.currentTarget.dataset.addDay === "1") {
          tempEndTimeOnNextDay = true
        }
        if (
          !e.currentTarget.classList.contains("no-avail-end-time") &&
          ((tempEndTimeOnNextDay && !startTimeOnNextDay) ||
            (tempEndTimeOnNextDay === startTimeOnNextDay &&
              tempEndTime > startTime))
        ) {
          endTime = tempEndTime
          endTimeOnNextDay = tempEndTimeOnNextDay

          let inputTextEndTime = document.getElementById("input-text-end-time")
          inputTextEndTime.innerText = endTime
          inputTextEndTime.classList.add("completed")

          document.querySelector(".time-modal").style.display = "none"
          try {
            document
              .getElementById("start-time-select")
              .classList.remove("highlighted")
          } catch (err) {}
          try {
            document
              .getElementById("end-time-select")
              .classList.remove("highlighted")
          } catch (err) {}

          try {
            const eventProperties = {
              "Selected Date": document.querySelector(".text-date-input-space")
                .value,
              "Start Time": startTime,
              "End Time": endTime,
            }
            if (startTimeOnNextDay) {
              eventProperties["Start Time On Next Day"] = true
            }
            if (endTimeOnNextDay) {
              eventProperties["End Time On Next Day"] = true
            }
            logAmplitudeEvent("Select End Time (Space)", eventProperties)
          } catch (err) {}

          onTimesChange()
        }
      }
    }

    function timeClear(retainTimes) {
      const timeButtons = document.getElementsByClassName("time-button")
      for (const tb of timeButtons) {
        tb.classList.remove("selected")
        tb.classList.remove("auto-selected")
        tb.classList.remove("disabled")
        util.removeButtonClassAndTooltip(tb, "no-avail-end-time")
        util.removeButtonClassAndTooltip(tb, "no-avail-start-time")
        tb.removeEventListener("mouseover", mouseoverSelectOtherTimes)
        tb.removeEventListener("mouseout", mouseoutSelectOtherTimes)
      }
      document.getElementById("time-modal-header").innerText =
        "Choose a start time"
      document.getElementById("time-modal-clear").style.display = "none"

      timeStatus = TIME_NONE

      // If end time was already selected, select start time
      if (
        document
          .getElementById("end-time-select")
          .classList.contains("highlighted")
      ) {
        document
          .getElementById("end-time-select")
          .classList.remove("highlighted")
        document
          .getElementById("start-time-select")
          .classList.add("highlighted")
      }

      if (!(retainTimes === true)) {
        startTime = undefined
        endTime = undefined

        let inputTextStartTime = document.getElementById(
          "input-text-start-time"
        )
        inputTextStartTime.innerText = "Choose time"
        inputTextStartTime.classList.remove("completed")
        let inputTextEndTime = document.getElementById("input-text-end-time")
        inputTextEndTime.innerText = "Choose time"
        inputTextEndTime.classList.remove("completed")
        try {
          document
            .getElementById("start-time-select")
            .classList.remove("highlighted")
        } catch (err) {}
        try {
          document
            .getElementById("end-time-select")
            .classList.remove("highlighted")
        } catch (err) {}
        setDateTimeText()
      }
    }

    function timeClose() {
      if (timeStatus === TIME_NONE) {
        try {
          document
            .getElementById("start-time-select")
            .classList.remove("highlighted")
        } catch (err) {}
      } else if (timeStatus === TIME_START) {
        if (endTime) {
          util.clickTime(endTime)
        } else {
          timeClear()
        }
      }
      document.querySelector(".time-modal").style.display = "none"

      try {
        const eventProperties = {
          "Selected Date": document.querySelector(".text-date-input-space")
            .value,
          "Start Time": startTime,
          "End Time": endTime,
        }
        logAmplitudeEvent("Close Time Modal (Space)", eventProperties)
      } catch (err) {}
    }

    // If just a start time is selected, as the user mouses over potential end times, colour all the times in between.
    function mouseoverSelectOtherTimes(e) {
      if (timeStatus === TIME_START) {
        let mouseoverTime = e.currentTarget.innerText
        const timeButtons = document.getElementsByClassName("time-button")
        for (const tb of timeButtons) {
          if (tb.innerText < mouseoverTime && tb.innerText > startTime) {
            tb.classList.add("auto-selected")
          }
        }
      }
    }

    function mouseoutSelectOtherTimes(e) {
      if (timeStatus === TIME_START) {
        let mouseoverTime = e.currentTarget.innerText
        const timeButtons = document.getElementsByClassName("time-button")
        for (const tb of timeButtons) {
          if (tb.innerText < mouseoverTime && tb.innerText > startTime) {
            tb.classList.remove("auto-selected")
          }
        }
      }
    }

    /**
     * Updates after date & time has been set by user
     */
    async function onTimesChange() {
      // First reset everything
      let pricingElem = document.getElementById("booking-section-pricing")
      pricingElem.style.display = "none"
      let errorElem = document.getElementById("booking-section-error")
      errorElem.style.display = "none"
      let explainerElem = document.getElementById("booking-section-explainer")
      explainerElem.style.display = "none"

      let diff = getEndDateTime().diff(getStartDateTime())
      let numHours = parseInt(diff.as("hours"))
      let numMins = diff.as("minutes") - numHours * 60
      let numHoursFixed = util.roundToMaxDp(numHours + numMins / 60, 1)
      let hourText = "hours"
      if (numHours == 1 && numMins == 0) {
        hourText = "hour"
      }
      let inputTextStartTime = document.getElementById("input-text-start-time")
      inputTextStartTime.innerText = startTime
      inputTextStartTime.classList.add("completed")
      let inputTextEndTime = document.getElementById("input-text-end-time")
      inputTextEndTime.innerText =
        endTime + " (" + numHoursFixed + " " + hourText + ")"
      document
        .getElementById("end-time-select")
        .classList.add("grow-15-desktop")
      inputTextEndTime.classList.add("completed")
      setDateTimeText()

      util.updateUrlParam("starttime", startTime.replace(":", ""), false, true)
      util.updateUrlParam("endtime", endTime.replace(":", ""), false, true)
      if (startTimeOnNextDay) {
        util.updateUrlParam("starttimeonnextday", 1, false, false)
      } else {
        util.deleteUrlParam("starttimeonnextday")
      }
      if (endTimeOnNextDay) {
        util.updateUrlParam("endtimeonnextday", 1, false, false)
      } else {
        util.deleteUrlParam("endtimeonnextday")
      }

      let reserveButton = document.getElementById("booking-section-button")
      if (enquiryOnly) {
        reserveButton.classList.add("hide")
        let enquiryButton = document.getElementById("enquiry-only-button")
        try {
          let sessionTime = new Date(date).toLocaleDateString("en-GB", {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric",
          })
          if (startTime && endTime) {
            sessionTime += " " + startTime + " - " + endTime
          }
          util.addUrlParamToLink(enquiryButton, "sessiontime", sessionTime)
        } catch (err) {}
        try {
          let amplitudeDeviceId = amplitude.getInstance().options.deviceId
          if (amplitudeDeviceId) {
            util.addUrlParamToLink(enquiryButton, "deviceid", amplitudeDeviceId)
          }
        } catch (err) {}
        enquiryButton.classList.remove("hide")
        return
      }

      // Update the list of addons when the times change (since some addons depend on the availability of another space)
      getCheckoutData()

      let totalPrice
      if (
        util.checkTimesAreAvailable(
          getStartDateTime().ts,
          getEndDateTime().ts,
          availableTimes
        )
      ) {
        try {
          totalPrice = await findSpacePricing(
            getStartDateTime(),
            getEndDateTime()
          )
        } catch (err) {
          errorElem.innerText =
            "There was an error getting the price for your selected times - please refresh or choose a different time."
          errorElem.style.display = "block"
          util.logToSentry(err)
          logAmplitudeEvent("Unknown Error Retrieving Session Price")
          return
        }
      } else {
        logAmplitudeEvent("No Availability For Selected Times")
      }
      if (!totalPrice) {
        let mismatch = durationMismatch(numHours, numMins)
        if (mismatch && mismatch.type === "belowMinDuration") {
          errorElem.innerText = `The venue doesn't accept bookings below ${mismatch.duration} hours - please extend your selection.`
        } else if (mismatch && mismatch.type === "aboveMaxDuration") {
          errorElem.innerText = `The venue doesn't accept bookings over ${mismatch.duration} hours - please change your selection.`
        } else {
          errorElem.innerText =
            "There is no availability for your selected times - please choose a different time."
        }
        errorElem.style.display = "block"
        return
      }
      const hourlyPrice = util.calculateHourlyPrice(
        totalPrice,
        numHours,
        numMins
      )
      pricingElem.style.display = "flex"
      document.getElementById("booking-section-price-detail").innerText =
        "£" + hourlyPrice + " x " + numHoursFixed + " " + hourText
      document.getElementById("booking-section-price-total").innerText =
        "£" + totalPrice
      updateCheckoutPrice()

      // Update price in header
      setFromPrice(hourlyPrice, false)

      reserveButton.innerText = "Reserve"
      document.getElementById("mobile-book-button").innerText = "Reserve"
      // Display Reserve explainer message
      if (
        allowPayLater &&
        1 == 2 /* Temporarily disable Pay Later */ &&
        util.initAbTest("payLater", "Pay Later", "paylater") === util.AB_ENABLED
      ) {
        document
          .getElementById("explainer-text-medium")
          .classList.remove("hide")
        document.getElementById("explainer-text-small").classList.add("hide")
      } else {
        if (document.getElementById("explainer-text-medium")) {
          document.getElementById("explainer-text-medium").classList.add("hide")
        }
        document.getElementById("explainer-text-small").classList.remove("hide")
      }
      explainerElem.style.display = "block"

      // Remove the "See availability" event listener that blocks the link (or the Amplitude event listener if this is running for 2nd+ time)
      var newReserveButton = reserveButton.cloneNode(true)
      reserveButton.parentNode.replaceChild(newReserveButton, reserveButton)

      // Set fields used by custom checkout
      checkoutNumHours = numHoursFixed
      checkoutTotalPrice = parseFloat(totalPrice)

      newReserveButton.addEventListener("click", function (e) {
        const eventProperties = {
          "Selected Date": document.querySelector(".text-date-input-space")
            .value,
          "Start Time": startTime,
          "End Time": endTime,
        }
        if (startTimeOnNextDay) {
          eventProperties["Start Time On Next Day"] = true
        }
        if (endTimeOnNextDay) {
          eventProperties["End Time On Next Day"] = true
        }
        logAmplitudeEvent("Press Reserve (Space)", eventProperties)

        logAmplitudeEvent("Press Reserve Custom Checkout Available")
        if (availableAddons.length > 0) {
          logAmplitudeEvent("Display Addons")
          util.updateUrlParam("addon", 1, { type: "addon" }, false)
          displayAddons()
        } else {
          logAmplitudeEvent("No Addons")
          util.updateUrlParam("checkout", 1, { type: "checkout" }, false)
          displayCheckout()
        }
        e.preventDefault()
      })
    }

    function getStartDateTime() {
      return util.calculateDateTime(date, startTime, startTimeOnNextDay)
    }

    function getEndDateTime() {
      return util.calculateDateTime(date, endTime, endTimeOnNextDay)
    }

    /**
     * New system pricing endpoint
     */
    async function findSpacePricing(startDateTime, endDateTime) {
      const startTimestamp = startDateTime.ts / 1000
      const endTimestamp = endDateTime.ts / 1000
      let params = `?start_timestamp=${startTimestamp}&end_timestamp=${endTimestamp}&slug=${slug}`
      if (searchParams.has("booker")) {
        params += `&encrypted_email=${searchParams.get("booker")}`
      }
      const priceData = await util.api
        .get(`get-session-price${params}`, { retry: { limit: 4 } })
        .json()
      return priceData.price
    }

    /* Check if the times match, but the minimum or maximum duration is not met */
    function durationMismatch(numHours, numMins) {
      if (date.length === 0) {
        return
      }
      const startTimestamp = getStartDateTime().ts
      const endTimestamp = getEndDateTime().ts
      const durationHours = numHours + numMins / 60
      let belowMinDuration = 0
      let matchingTimes = availableTimes.filter(
        (t) =>
          t.start_time_utc <= startTimestamp && t.end_time_utc >= endTimestamp
      )
      let durationMatches = matchingTimes.filter(
        (t) => durationHours >= t.min_at_hours
      )
      if (durationMatches.length > 0) {
        return
      } else {
        durationMatches = matchingTimes.filter(
          (t) => durationHours < t.min_at_hours
        )
        if (durationMatches.length > 0) {
          // get min of all min_at_hours in durationMatches
          belowMinDuration = durationMatches.reduce(
            (min, t) => (t.min_at_hours < min ? t.min_at_hours : min),
            durationMatches[0].min_at_hours
          )
          return {
            type: "belowMinDuration",
            duration: belowMinDuration,
          }
        }
      }
    }

    function displayAddons() {
      let divAddonItems = document.getElementById("add-on-items")
      // Remove any existing addons (happens on mobile if addons were shown then modal closed)
      while (divAddonItems.childNodes.length > 1) {
        divAddonItems.removeChild(divAddonItems.lastChild)
      }
      let divAddonItem = document.getElementsByClassName("add-on-item")[0]
      availableAddons.forEach(function (addon) {
        let newAddon = divAddonItem.cloneNode(true)
        newAddon.querySelector("input").setAttribute("addon_id", addon.id)
        let addonTextDivs = newAddon
          .getElementsByClassName("checkbox-item-text")[0]
          .querySelectorAll("div")
        addonTextDivs[0].innerText = addon.name
        let addonPrice = parseFloat(addon.price)
        if (addon.is_hourly_price) {
          addonPrice = addonPrice * checkoutNumHours
        }
        addonPrice = addonPrice.toFixed(2)
        addonTextDivs[1].innerText = "£" + addonPrice
        addonTextDivs[1].dataset.price = addonPrice
        addonTextDivs[1].dataset.is_hourly_price = addon.is_hourly_price
        newAddon.querySelector("input").addEventListener("change", function () {
          if (this.checked) {
            logAmplitudeEvent("Addon Selected", {
              "Addon ID": addon.id,
              "Addon Name": addon.name,
              "Addon Price": addonPrice,
            })
          } else {
            logAmplitudeEvent("Addon Unselected", {
              "Addon ID": addon.id,
              "Addon Name": addon.name,
              "Addon Price": addonPrice,
            })
          }
        })
        newAddon.classList.remove("hide")
        divAddonItems.appendChild(newAddon)
      })
      document.getElementById("booking-section-inner").classList.add("hide")
      document.getElementById("addons-form").classList.remove("hide")
    }

    function displayCheckoutAddons() {
      let grandTotal = checkoutTotalPrice
      let basketAddons = document.getElementById("basket-addons")
      for (const addon of selectedAddons) {
        grandTotal += parseFloat(addon.price)
        const newAddonDiv = document.createElement("div")
        basketAddons.insertBefore(newAddonDiv, basketAddons.lastChild)
        newAddonDiv.classList.add("flex-between-10gap")
        const addonNameDiv = document.createElement("div")
        newAddonDiv.appendChild(addonNameDiv)
        addonNameDiv.innerText = addon.name
        const addonPriceDiv = document.createElement("div")
        newAddonDiv.appendChild(addonPriceDiv)
        addonPriceDiv.innerText = "£" + parseFloat(addon.price).toFixed(2)
      }
      if (selectedAddons.length > 0) {
        document.getElementById("basket-grand-total").innerText =
          "£" + parseFloat(grandTotal).toFixed(2)
        basketAddons.classList.remove("hide")
      }
    }

    function updateCheckoutPrice() {
      document.getElementById("basket-price-detail").innerText =
        document.getElementById("booking-section-price-detail").innerText
      document.getElementById("basket-price-total").innerText =
        document.getElementById("booking-section-price-total").innerText
      // Display the extend by 30 mins section if the time is available
      if (priceIncludingExtra30Mins) {
        let currentPrice = document
          .getElementById("booking-section-price-total")
          .innerText.replace(/^£/, "")
        if (currentPrice) {
          // Don't show extra half hour option if there are hourly addons
          // selected, or if is_hourly_price isn't specified for some reason.
          // I don't think the benefit of showing the extra 30 mins in this
          // scenario is worth the cost to implement it yet.
          for (const addon of selectedAddons) {
            if (addon.is_hourly_price !== "false") {
              return
            }
          }
          currentPrice = parseFloat(currentPrice)
          let extendedPrice = parseFloat(priceIncludingExtra30Mins)
          priceOfExtra30Mins = (extendedPrice - currentPrice).toFixed(2)
          document.getElementById("price-for-extra-30-mins").innerText =
            priceOfExtra30Mins
          document.getElementById("extend-yes").required = true
          document.getElementById("extend-no").required = true
          document.getElementById("extend-30-mins").classList.remove("hide")
          // Initially hide the cleaning checkbox initially - it will be
          // revealed after the user makes a choice re adding 30 mins
          document
            .getElementById("cleaning-checkbox-field")
            .classList.add("hide")
        }
      }
    }

    function add30MinsExtension() {
      let grandTotal = checkoutTotalPrice + parseFloat(priceOfExtra30Mins)
      for (const addon of selectedAddons) {
        grandTotal += parseFloat(addon.price)
      }
      document.getElementById("basket-grand-total").innerText =
        "£" + parseFloat(grandTotal).toFixed(2)

      let basketAddons = document.getElementById("basket-addons")
      const newAddonDiv = document.createElement("div")
      basketAddons.insertBefore(newAddonDiv, basketAddons.lastChild)
      newAddonDiv.id = "addon-extend-30-mins"
      newAddonDiv.classList.add("flex-between-10gap")
      const addonNameDiv = document.createElement("div")
      newAddonDiv.appendChild(addonNameDiv)
      addonNameDiv.innerText =
        "Extend booking to " +
        document.getElementById("extended-time").innerText
      const addonPriceDiv = document.createElement("div")
      newAddonDiv.appendChild(addonPriceDiv)
      addonPriceDiv.innerText = "£" + priceOfExtra30Mins
      basketAddons.classList.remove("hide")
      document.getElementById("cleaning-checkbox-time").innerText =
        document.getElementById("extended-time").innerText
    }

    function remove30MinsExtension() {
      document.getElementById("cleaning-checkbox-time").innerText = endTime
      if (!document.getElementById("addon-extend-30-mins")) {
        return
      }
      let grandTotal = checkoutTotalPrice
      for (const addon of selectedAddons) {
        grandTotal += parseFloat(addon.price)
      }
      document.getElementById("basket-grand-total").innerText =
        "£" + parseFloat(grandTotal).toFixed(2)
      document.getElementById("addon-extend-30-mins").remove()
      // Don't bother hiding the basket-addons div if there are no other addons
    }

    async function displayCheckout() {
      document.getElementById("space-content").style.display = "none"
      document.getElementById("checkout-content").style.display = "block"
      try {
        // Temporary: in case this doesn't exist
        document.getElementById("viewing-content").classList.add("hide")
        document.getElementById("enquiry-content").classList.add("hide")
      } catch (err) {}
      hideMobileBookModal()

      // Clone the review stars, remove the hyperlink and reduce the size of the stars
      let starsTotalClone = document
        .getElementById("stars-total")
        .cloneNode(true)
      starsTotalClone.removeAttribute("href")
      starsTotalClone.setAttribute("id", "basket-stars-total")
      starsTotalClone.querySelectorAll("svg").forEach(function (svg) {
        svg.setAttribute("width", "14px")
      })
      starsTotalClone.querySelector("div").style.marginBottom = "-2px"
      document
        .getElementById("basket-space-name-and-reviews")
        .appendChild(starsTotalClone)

      document.getElementById("basket-date").innerText =
        document.getElementById("input-text-date").innerText
      document.getElementById("basket-time").innerText =
        startTime + " - " + endTime

      document.getElementById("cleaning-checkbox-time").innerText = endTime
      let endDateTimePlus30 = getEndDateTime().plus({ minutes: 30 })
      if (
        util.checkTimesAreAvailable(
          getStartDateTime().ts,
          endDateTimePlus30.ts,
          availableTimes
        )
      ) {
        try {
          priceIncludingExtra30Mins = await findSpacePricing(
            getStartDateTime(),
            endDateTimePlus30
          )
          document.getElementById("extended-time").innerText =
            endDateTimePlus30.toFormat("HH:mm")
        } catch (err) {
          // If /get-session-price fails, priceIncludingExtra30Mins will remain
          // undefined and the 30 mins extension won't be shown
          util.logToSentry(err)
          logAmplitudeEvent("Unknown Error Retrieving Session Price")
        }
      }
      updateCheckoutPrice()

      if (
        allowPayLater &&
        1 == 2 /* Temporarily disable Pay Later */ &&
        util.initAbTest("payLater", "Pay Later", "paylater") === util.AB_ENABLED
      ) {
        document
          .getElementById("booking-options-section")
          .classList.remove("hide")
        document.getElementById("pay-now").required = true
        document.getElementById("pay-later").required = true
        document.getElementById("venue-visit-yes").required = true
        document.getElementById("venue-visit-no").required = true
        document.getElementById("viewing-section").classList.add("hide")
        document
          .getElementById("booking-options-confirmation")
          .classList.add("hide")
        document.getElementById("checkout-cancel-section").classList.add("hide")
        document.getElementById("payment-icons").classList.add("hide")
      } else {
        document.getElementById("booking-options-section").classList.add("hide")
        document.getElementById("pay-now").required = false
        document.getElementById("pay-later").required = false
        document.getElementById("venue-visit-yes").required = false
        document.getElementById("venue-visit-no").required = false
        document.getElementById("viewing-section").classList.remove("hide")
        document
          .getElementById("booking-options-confirmation")
          .classList.add("hide")
        document.getElementById("payment-icons").classList.remove("hide")
      }

      // scroll to the top of the page
      window.scrollTo(0, 0)

      logAmplitudeEvent("Checkout Displayed")
    }

    function displayTimeModal(selectStartTime) {
      timeClear(true)
      setTimeAvailability()

      document.querySelector(".time-modal").style.display = "flex"
      document.getElementById("start-time-select").classList.add("highlighted")
      document.getElementById("end-time-select").classList.remove("highlighted")

      /* If the user has already chosen a start and end time, and they click on
      the end time, we assume they just want to change the end time so we
      reselect their start time first. */
      if (!selectStartTime && startTime && endTime) {
        util.clickTime(startTime)
      }
    }

    function displayMobileBookModal() {
      let mobileBookModal = document.getElementById("mobile-book-modal")
      mobileBookModal.style.display = "block"
      mobileBookModal.appendChild(
        document.getElementById("booking-section-inner")
      )
      mobileBookModal.appendChild(document.getElementById("addons-form"))
      try {
        // On desktop we hide the original recurring link and show the button. On mobile
        // we move the button to the booking screen and still show the (mobile-only) link.
        mobileBookModal.appendChild(document.getElementById("recurring-button"))
      } catch (err) {}
    }

    function hideMobileBookModal() {
      document.getElementById("mobile-book-modal").style.display = "none"
      let bookingSection = document.getElementById("booking-section")
      let bookingSectionInner = document.getElementById("booking-section-inner")
      let addonsForm = document.getElementById("addons-form")
      bookingSection.appendChild(bookingSectionInner)
      bookingSection.appendChild(addonsForm)
      bookingSectionInner.classList.remove("hide")
      addonsForm.classList.add("hide")
      try {
        document
          .getElementById("booking-widget-col")
          .appendChild(document.getElementById("recurring-button"))
      } catch (err) {}
    }

    function seeAvailClick(e) {
      if (!date || (startTime && endTime)) {
        fp.open()
      } else {
        displayTimeModal(true)
      }
      try {
        const eventProperties = {
          "Selected Date": document.querySelector(".text-date-input-space")
            .value,
          "Start Time": startTime,
          "End Time": endTime,
        }
        if (endTimeOnNextDay) {
          eventProperties["End Time On Next Day"] = true
        }
        if (startTimeOnNextDay) {
          eventProperties["Start Time On Next Day"] = true
        }
        logAmplitudeEvent("See Availability (Space)", eventProperties)
      } catch (err) {}
      e.preventDefault()
    }

    function setFromPrice(price, displayFrom) {
      if (!price) {
        document.getElementById("booking-section-header").classList.add("hide")
        logAmplitudeEvent("No Pricing For Space")
        logNoPricingOrAvailToSentry()
        return
      }
      document.getElementById("booking-section-header-price").innerText =
        "£" + util.roundToMaxDp(price, 2)
      document.getElementById("price-block-price").innerText =
        "£" + util.roundToMaxDp(price, 2)
      document.getElementById("old-price").innerText =
        "£" + util.roundToMaxDp(price, 2)
      let headerLeft = document.getElementById("booking-section-header-left")
      let mobilePriceBlockFrom = document.getElementById("price-block-from")
      let mobilePriceBlockDate = document.getElementById("price-block-date")
      if (displayFrom) {
        headerLeft.style.display = "block"
        mobilePriceBlockFrom.style.display = "block"
        mobilePriceBlockDate.style.display = "none"
      } else {
        headerLeft.style.display = "none"
        mobilePriceBlockFrom.style.display = "none"
        mobilePriceBlockDate.style.display = "block"
      }
    }

    function logNoPricingOrAvailToSentry() {
      if (
        document.getElementsByClassName("availability-hidden")[0].innerText ==
        "public"
      ) {
        util.logToSentry("No pricing or availability for public space " + slug)
      }
    }

    /**
     * Get the appropriate checkout question ID for a given form field.
     * @param element The form field element.
     * @returns {number}
     */
    function getQuestionIdFromElement(element) {
      return element.getAttribute("question_id")
    }

    async function getCheckoutData() {
      let startDateTime = DateTime.fromISO(date + "T" + startTime, {
        zone: util.TIMEZONE,
      })
      if (startTimeOnNextDay) {
        startDateTime = startDateTime.plus({ days: 1 })
      }
      let endDateTime = DateTime.fromISO(date + "T" + endTime, {
        zone: util.TIMEZONE,
      })
      if (endTimeOnNextDay) {
        endDateTime = endDateTime.plus({ days: 1 })
      }
      let payload = {
        space_slug: slug,
        using_new_system: true,
        start_time: startDateTime,
        end_time: endDateTime,
      }
      let checkoutData
      try {
        const data = await util.api
          .post("checkout-data", {
            json: payload,
            retry: {
              limit: 4,
            },
          })
          .json()
        checkoutData = data.checkout_data
      } catch (error) {
        logAmplitudeEvent("Error Calling checkout-data")
        util.logToSentry(error)
        return
      }
      availableAddons = checkoutData.addons
      return checkoutData
    }

    async function canCustomCheckoutBeUsed() {
      let checkoutData = await getCheckoutData()

      standardCheckoutQuestionIds =
        util.getQuestionIdsFromElements("question_id")
      let questions
      let eventTypeSelect = document.getElementById("event-type")
      let marketingChannelSelect = document.getElementById("marketing-channel")
      let eventTypeOptions = []
      let marketingChannelOptions = []
      questions = checkoutData.questions

      let eventTypeQuestions = questions.filter(
        (q) => q.id === parseInt(eventTypeSelect.getAttribute("question_id"))
      )
      if (eventTypeQuestions.length > 0) {
        eventTypeOptions = eventTypeQuestions[0].options.map((o) => o.option)
      } else {
        // E.g. if there is a question_map_exclude for this space
        eventTypeSelect.removeAttribute("field_required")
        eventTypeSelect.parentElement.classList.add("hide")
      }
      let marketingChannelQuestions = questions.filter(
        (q) =>
          q.id === parseInt(marketingChannelSelect.getAttribute("question_id"))
      )
      if (marketingChannelQuestions.length > 0) {
        marketingChannelOptions = marketingChannelQuestions[0].options.map(
          (o) => o.option
        )
      } else {
        marketingChannelSelect.removeAttribute("field_required")
        marketingChannelSelect.parentElement.classList.add("hide")
      }
      let numGuestsInput = document.getElementById("num-guests")
      let numGuestsQuestion = questions.filter(
        (q) => q.id === parseInt(numGuestsInput.getAttribute("question_id"))
      )
      if (numGuestsQuestion.length == 0) {
        document
          .getElementById("max-people-checkbox")
          .removeAttribute("field_required")
        document
          .getElementById("max-people-checkbox")
          .parentElement.parentElement.classList.add("hide")
      }
      try {
        // Add a DOMPurify hook to open all links in a new tab
        DOMPurify.addHook("afterSanitizeAttributes", function (node) {
          if ("target" in node) {
            node.setAttribute("target", "_blank")
            // prevent https://www.owasp.org/index.php/Reverse_Tabnabbing
            node.setAttribute("rel", "noopener noreferrer")
            return node
          }
        })
      } catch (err) {
        util.logToSentry(err)
      }

      let venueQuestionSection = document.getElementById("venue-questions")
      // Add venue-specific questions
      for (const question of questions) {
        if (!standardCheckoutQuestionIds.includes(question.id)) {
          let questionTextAndControl
          if (question.type === "checkbox") {
            questionTextAndControl = document.createElement("label")
            questionTextAndControl.classList.add("w-checkbox")
            questionTextAndControl.classList.add("checkbox-field")
            let questionInput = document.createElement("input")
            questionInput.type = "checkbox"
            questionInput.classList.add("w-checkbox-input")
            questionInput.classList.add("checkbox")
            questionInput.required = question.is_required
            questionInput.setAttribute("question_id", question.id)
            let questionLabel = document.createElement("span")
            questionLabel.classList.add("checkbox-label")
            questionLabel.classList.add("w-form-label")
            let questionHTML = question.question
            try {
              // We allow question text to include markdown - it is
              // parsed with marked then DOMPurify
              questionHTML = DOMPurify.sanitize(
                marked.parseInline(question.question)
              )
            } catch (err) {
              util.logToSentry(err)
            }
            questionLabel.innerHTML =
              questionHTML +
              (question.is_required ? " <span class='red'>*</span>" : "")
            questionTextAndControl.appendChild(questionInput)
            questionTextAndControl.appendChild(questionLabel)
          } else {
            let questionControl
            if (question.type === "options") {
              questionControl = document.createElement("select")
              questionControl.setAttribute("question_id", question.id)
              questionControl.classList.add("select-default")
              questionControl.classList.add("w-select")
              questionControl.required = question.is_required
              let option = document.createElement("option")
              option.value = ""
              option.disabled = true
              option.selected = true
              option.innerText = "Select an option..."
              questionControl.appendChild(option)
              question.options.forEach(function (option) {
                let optionElem = document.createElement("option")
                optionElem.value = option.option
                optionElem.innerText = option.option
                questionControl.appendChild(optionElem)
              })
            } else if (question.type === "text") {
              questionControl = document.createElement("textarea")
              questionControl.setAttribute("question_id", question.id)
              questionControl.classList.add("textarea-default")
              questionControl.classList.add("w-input")
              questionControl.required = question.is_required
              questionControl.maxLength = 499
              if (question.placeholder) {
                questionControl.placeholder =
                  question.placeholder +
                  (question.is_required ? "" : " (optional)")
              }
            }
            questionTextAndControl = document.createElement("div")
            questionTextAndControl.classList.add("text-and-input")
            let questionText = document.createElement("div")
            let questionHTML = question.question
            try {
              questionHTML = DOMPurify.sanitize(
                marked.parseInline(question.question)
              )
            } catch (err) {
              util.logToSentry(err)
            }
            questionText.innerHTML =
              questionHTML +
              (question.is_required ? " <span class='red'>*</span>" : "")
            questionTextAndControl.appendChild(questionText)
            questionTextAndControl.appendChild(questionControl)
          }
          // Add as second last element
          venueQuestionSection.insertBefore(
            questionTextAndControl,
            venueQuestionSection.lastChild
          )
          venueQuestionSection.classList.remove("hide")
        }
      }

      let capacity = document.querySelector(".capacity-hidden").innerText
      let maxPeople = document.getElementById("max-people-text")
      maxPeople.innerText = maxPeople.innerText.replace("?", capacity)
      let maxPeopleLabel = document.getElementById("max-people-checkbox-label")
      maxPeopleLabel.innerHTML = maxPeopleLabel.innerHTML.replace("?", capacity)

      let option = document.createElement("option")
      option.value = ""
      option.disabled = true
      option.selected = true
      option.innerText = "Select an event type..."
      eventTypeSelect.appendChild(option)
      eventTypeOptions.forEach(function (eventType) {
        let option = document.createElement("option")
        option.value = eventType
        option.innerText = eventType
        eventTypeSelect.appendChild(option)
      })
      option = document.createElement("option")
      option.value = ""
      option.disabled = true
      option.selected = true
      option.innerText = "Select an option..."
      marketingChannelSelect.appendChild(option)
      marketingChannelOptions.forEach(function (marketingChannel) {
        let option = document.createElement("option")
        option.value = marketingChannel
        option.innerText = marketingChannel
        marketingChannelSelect.appendChild(option)
      })

      // Set required on for all visible input and select elements within checkout-form
      let checkoutFormInputs = document
        .getElementById("checkout-form")
        .querySelectorAll("input, select, textarea")
      checkoutFormInputs.forEach(function (elem) {
        if (elem.classList.contains("hide")) {
          elem.classList.remove("hide")
          elem.type = "hidden"
        } else if (elem.hasAttribute("field_required")) {
          elem.required = true
        }
      })

      // Add listeners to all the inputs
      try {
        // When someone selects the Other option in the event type dropdown, show the other event type field
        document
          .getElementById("event-type")
          .addEventListener("change", function (e) {
            let selectedEventType =
              e.target.options[e.target.selectedIndex].value
            let eventTypeOther = document.getElementById("event-type-other")
            if (selectedEventType === "Other") {
              eventTypeOther.type = "text"
              eventTypeOther.required = true
            } else {
              eventTypeOther.type = "hidden"
              eventTypeOther.required = false
              eventTypeOther.innerText = ""
            }
            let alcoholSection = document.getElementById("alcohol-section")
            if (
              selectedEventType === "Teens' party" ||
              selectedEventType === "Adults' party" ||
              selectedEventType === "Wake or memorial service"
            ) {
              alcoholSection.classList.remove("hide")
              document.getElementById("alcohol-yes").required = true
              document.getElementById("alcohol-no").required = true
            } else {
              alcoholSection.classList.add("hide")
              document.getElementById("alcohol-yes").required = false
              document.getElementById("alcohol-no").required = false
            }
            let maxPeopleText = document.getElementById("max-people-text")
            maxPeopleText.innerText = maxPeopleText.innerText.replace(
              " (including kids)",
              ""
            )
            if (selectedEventType === "Kids' party") {
              maxPeopleText.innerText += " (including kids)"
            }
            logAmplitudeEvent("Event Type Selected", {
              "Event Type": selectedEventType,
            })
          })
        document
          .getElementById("event-type-other")
          .addEventListener("change", function (e) {
            logAmplitudeEvent("Event Type Other Input Changed", {
              "Input Value": this.value,
            })
          })
        document
          .getElementById("num-guests")
          .addEventListener("change", function (e) {
            logAmplitudeEvent("Number of People Input Changed", {
              "Input Value": this.value,
            })
          })
        if (document.querySelector('input[name="visit"]')) {
          document.querySelectorAll('input[name="visit"]').forEach((elem) => {
            elem.addEventListener("change", function (event) {
              if (event.target.value === "yes") {
                document
                  .getElementById("visit-secondary")
                  .classList.remove("hide")
                document.getElementById("visit-secondary").innerText =
                  "We will arrange a visit after you book"
              } else {
                document.getElementById("visit-secondary").classList.add("hide")
              }
              logAmplitudeEvent("Venue Visit Selection Changed", {
                "Input Value": event.target.value,
              })
            })
          })
        }
        if (document.querySelector('input[name="venue-visit"]')) {
          document
            .querySelectorAll('input[name="venue-visit"]')
            .forEach((elem) => {
              elem.addEventListener("change", function (event) {
                if (event.target.value === "yes") {
                  document
                    .getElementById("booking-option-viewing")
                    .classList.remove("hide")
                } else {
                  document
                    .getElementById("booking-option-viewing")
                    .classList.add("hide")
                }
                logAmplitudeEvent("Venue Viewing Selection Changed", {
                  "Input Value": event.target.value,
                })

                if (document.querySelector('input[name="pay"]:checked')) {
                  document
                    .getElementById("booking-options-confirmation")
                    .classList.remove("hide")
                }
              })
            })
        }
        if (document.querySelector('input[name="pay"]')) {
          document.querySelectorAll('input[name="pay"]').forEach((elem) => {
            elem.addEventListener("change", function (event) {
              if (event.target.value === "later") {
                document
                  .getElementById("booking-option-pay-later")
                  .classList.remove("hide")
              } else {
                document
                  .getElementById("booking-option-pay-later")
                  .classList.add("hide")
              }
              logAmplitudeEvent("Payment Option Selection Changed", {
                "Input Value": event.target.value,
              })
              if (document.querySelector('input[name="venue-visit"]:checked')) {
                document
                  .getElementById("booking-options-confirmation")
                  .classList.remove("hide")
              }
            })
          })
        }
        if (document.querySelector('input[name="alcohol"]')) {
          document.querySelectorAll('input[name="alcohol"]').forEach((elem) => {
            elem.addEventListener("change", function (event) {
              logAmplitudeEvent("Alcohol Selection Changed", {
                "Input Value": event.target.value,
              })
            })
          })
        }
        document
          .getElementById("furniture-equipment")
          .addEventListener("change", function (e) {
            logAmplitudeEvent("Furniture and Equipment Input Changed", {
              "Input Value": this.value,
            })
          })
        document
          .getElementById("first-name")
          .addEventListener("change", function (e) {
            logAmplitudeEvent("First Name Input Changed")
          })
        document
          .getElementById("last-name")
          .addEventListener("change", function (e) {
            logAmplitudeEvent("Last Name Input Changed")
          })
        document
          .getElementById("marketing-channel")
          .addEventListener("change", function (e) {
            let selectedMarketingChannel =
              e.target.options[e.target.selectedIndex].value
            logAmplitudeEvent("Marketing Channel Selected", {
              "Marketing Channel": selectedMarketingChannel,
            })
          })
        document
          .getElementById("cleaning-checkbox")
          .addEventListener("change", function (e) {
            logAmplitudeEvent("Cleaning Checkbox Input Changed", {
              "Input Value": e.currentTarget.checked,
            })
          })
        document
          .getElementById("max-people-checkbox")
          .addEventListener("change", function (e) {
            logAmplitudeEvent("Max People Checkbox Input Changed", {
              "Input Value": e.currentTarget.checked,
            })
            let numGuests = document.getElementById("num-guests-field")
            if (e.currentTarget.checked) {
              numGuests.classList.remove("hide")
              document.getElementById("num-guests").focus()
            } else {
              numGuests.classList.add("hide")
            }
          })
        document.getElementById("phone").addEventListener("keyup", function () {
          validatePhone(false)
        })
        document.getElementById("email").addEventListener("keyup", function () {
          validateEmail(false)
        })
        intlTelInputValue = window.intlTelInput(
          document.querySelector("#phone"),
          {
            initialCountry: "gb",
          }
        )
      } catch (err) {
        util.logToSentry(err)
      }

      logAmplitudeEvent("Custom Checkout Is Available")

      try {
        if (availableAddons.length > 0) {
          logAmplitudeEvent("Addons Are Available")
        }
      } catch (err) {}
      try {
        document
          .getElementById("checkout-amenities")
          .addEventListener("click", (e) => {
            openModal("all-amenities-modal")
            logAmplitudeEvent("Open Checkout Amenities Modal")
          })
      } catch (err) {
        util.logToSentry(err)
        if (document.getElementById("all-amenities")) {
          if (document.getElementById("checkout-content")) {
            util.logToSentry(
              "checkout-amenities does not exist. all-amenities and checkout-content do exist"
            )
          } else {
            util.logToSentry(
              "checkout-amenities and checkout-content do not exist. all-amenities does exist"
            )
          }
        } else {
          util.logToSentry("checkout-amenities and all-amenities do not exist.")
        }
      }

      displayCheckoutAddons()

      if (checkoutData.viewing_times) {
        document.getElementById("viewing-availability").innerText =
          checkoutData.viewing_times
      } else {
        document.getElementById("viewing-availability").classList.add("hide")
        document
          .getElementById("viewing-availability-title")
          .classList.add("hide")
      }
    }

    function validateEmail(isFormBeingSubmitted) {
      removeEmailMessage()

      const address = document.getElementById("email").value.trim()

      if (isFormBeingSubmitted && !isValidEmail(address)) {
        // We only validate emails when submitting, because otherwise this shows up
        // while the user is typing
        renderEmailMessage(`Email address is invalid`, isFormBeingSubmitted)
        return false
      }

      const suggestion = emailSpellChecker.run({
        email: address,
      })
      if (suggestion) {
        renderEmailMessage(
          `Did you mean ${suggestion.full}?`,
          false // Don't scroll to the email field on suggestions
        )
      }

      // A suggestion is not guaranteed to be an error so we let the user submit here
      return true
    }

    function removeEmailMessage() {
      document.getElementById("email").classList.remove("red")
      const emailError = document.getElementById("email-error")
      if (emailError) {
        emailError.remove()
      }
    }

    function renderEmailMessage(message, scrollTo) {
      const html = `<div id="email-error" class="checkout-error-message">${message}</div>`
      const email = document.getElementById("email")
      email.classList.add("red")
      email.insertAdjacentHTML("afterend", html)
      if (scrollTo) {
        window.scrollTo(0, email.offsetTop)
      }
    }

    function validatePhone(isFormBeingSubmitted) {
      hidePhoneErrorMessage()
      if (
        isFormBeingSubmitted &&
        !document.getElementById("phone").value.trim()
      ) {
        if (!document.getElementById("phone").required) {
          return true
        }
        displayPhoneErrorMessage(
          "A phone number is required",
          isFormBeingSubmitted
        )
        return false
      }
      if (intlTelInputValue && !intlTelInputValue.isValidNumber()) {
        const errorCode = intlTelInputValue.getValidationError()
        if ((errorCode === 2 || errorCode === 4) && !isFormBeingSubmitted) {
          return
        }
        const errorMap = [
          "Your phone number is invalid",
          "The country code is invalid",
          "Your phone number is too short",
          "Your phone number is too long",
          "Your phone number is invalid",
        ]
        const msg = errorMap[errorCode] || "Your phone number is invalid"
        displayPhoneErrorMessage(msg, isFormBeingSubmitted)
        return false
      }
      return true
    }

    function displayPhoneErrorMessage(message, autoScroll) {
      document.getElementById("phone").classList.add("red")
      document.getElementById("phone-error").innerText = message
      document.getElementById("phone-error").classList.remove("hide")
      if (autoScroll) {
        window.scrollTo(0, document.getElementById("phone-container").offsetTop)
      }
    }

    function hidePhoneErrorMessage() {
      document.getElementById("phone-error").classList.add("hide")
      document.getElementById("phone").classList.remove("red")
    }

    // Save availability data in global var spacesJson then postAvailabilityLoad.
    async function getSpaceAvailability() {
      try {
        spacesJson = await util.api
          .get(`get-space-availability-combined?slug=${slug}`, {
            retry: { limit: 4 },
          })
          .json()
        postAvailabilityLoad()
      } catch (error) {
        util.logToSentry(error)
        logAmplitudeEvent("Unknown Error Retrieving Space Availability")
        if (enquiryOnly) {
          let enquiryOnlyButton = document.getElementById("enquiry-only-button")
          if (
            window
              .getComputedStyle(document.getElementById("mobile-book-sticky"))
              .getPropertyValue("display") != "none"
          ) {
            let mobileBookButton = document.getElementById("mobile-book-button")
            mobileBookButton.parentNode.insertBefore(
              enquiryOnlyButton,
              mobileBookButton
            )
          }
          enquiryOnlyButton.classList.remove("hide")
          let priceHtml = document.getElementById("old-price").innerHTML
          let parts = priceHtml.split("/script>")
          let priceText = parts[parts.length - 1]
          document.getElementById("price-block-price").innerText = priceText
          return
        }
        document
          .getElementById("after-booking-section")
          .classList.remove("hide")
      }
    }

    function postAvailabilityLoad() {
      if (spacesJson.length === 0) {
        logAmplitudeEvent("No Availability For Space")
        logNoPricingOrAvailToSentry()
        return
      }
      // Set this first - if time query params were passed it will be updated again
      setFromPrice(spacesJson[0].min_price_per_hour_space, true)
      prepareBookingSection()
      canCustomCheckoutBeUsed()
    }

    /**
     * Set up the booking section (date and time pickers etc)
     */
    function prepareBookingSection() {
      document.getElementById("booking-section").classList.remove("hide")
      document.getElementById("mobile-book-button").classList.remove("hide")
      document.getElementById("after-booking-section").classList.remove("hide")

      document.getElementById("cancel-no-time-filter").classList.add("hide")
      document.getElementById("cancel-time-filter").classList.remove("hide")

      // Check if flatpickr already exists - if so assume this has been run before
      if (!fp) {
        let reserveButton = document.getElementById("booking-section-button")
        reserveButton.addEventListener("click", seeAvailClick)
        document
          .getElementById("mobile-book-button")
          .addEventListener("click", displayMobileBookModal)
        document
          .getElementById("price-block-date")
          .addEventListener("click", displayMobileBookModal)
        document
          .getElementById("mobile-book-modal-close")
          .addEventListener("click", hideMobileBookModal)

        // Flatpickr calendar
        let dateArray = spacesJson[0].dates.map((s) => s["start_date"])

        fp = flatpickr(".flatpickr", {
          enableTime: false,
          wrap: true,
          minDate: dateArray.sort()[0],
          maxDate: dateArray.sort().reverse()[0],
          disableMobile: "true",
          monthSelectorType: "static",
          locale: {
            firstDayOfWeek: 1, // start week on Monday
          },
          enable: dateArray,
          onChange: function (selectedDates, dateStr, instance) {
            flatpickrDateChange(selectedDates, dateStr, instance)
          },
          onOpen: flatpickrPosition,
        })

        document
          .getElementById("start-time-select")
          .addEventListener("click", function (e) {
            if (date) {
              displayTimeModal(true)
            } else {
              fp.open()
            }
            const eventProperties = {
              "Selected Date": document.querySelector(".text-date-input-space")
                .value,
            }
            logAmplitudeEvent("Open Start Time (Space)", eventProperties)
          })
        document
          .getElementById("end-time-select")
          .addEventListener("click", function (e) {
            if (date) {
              if (
                !document
                  .getElementById("end-time-select")
                  .classList.contains("highlighted")
              ) {
                displayTimeModal(false)
              }
            } else {
              fp.open()
            }
            logAmplitudeEvent("Open End Time (Space)")
          })

        // Add listeners for Amplitude
        document
          .getElementById("date-select")
          .addEventListener("click", function (e) {
            logAmplitudeEvent("Open Choose Date (Space)")
          })

        // Add listeners to the time modal buttons.
        let elem = document.getElementById("time-modal-clear")
        elem.addEventListener("click", function () {
          timeClear()
          try {
            const eventProperties = {
              "Selected Date": document.querySelector(".text-date-input-space")
                .value,
              "Start Time": startTime,
              "End Time": endTime,
            }
            if (startTimeOnNextDay) {
              eventProperties["Start Time On Next Day"] = true
            }
            if (endTimeOnNextDay) {
              eventProperties["End Time On Next Day"] = true
            }
            logAmplitudeEvent("Clear Times (Space)", eventProperties)
          } catch (err) {}
        })
        elem = document.getElementById("time-modal-close")
        elem.addEventListener("click", timeClose)

        if (searchParams.has("date")) {
          fp.setDate(date)
          flatpickrDateChange(fp.selectedDates, undefined, undefined, false)
        }

        try {
          let targetNode =
            document.getElementsByClassName("flatpickr-calendar")[0]
          let flatpickrObserver = new MutationObserver(function () {
            if (targetNode.classList.contains("open")) {
              document
                .getElementById("date-select")
                .classList.add("highlighted")
            } else {
              document
                .getElementById("date-select")
                .classList.remove("highlighted")
            }
          })
          flatpickrObserver.observe(targetNode, {
            attributes: true,
            childList: true,
          })
        } catch (err) {
          util.logToSentry(err)
        }

        try {
          if (searchParams.has("checkout")) {
            logAmplitudeEvent("Checkout Query Parameter Found")
            if (searchParams.has("addons")) {
              selectedAddons = JSON.parse(
                decodeURIComponent(searchParams.get("addons"))
              )
              logAmplitudeEvent("Addons Query Parameter Found")
            }
            // Flow is: Space -> Checkout -> Stripe
            // When user goes back from Stripe to Checkout, page loads so
            // there is no saved state. Fake some so page will refresh when
            // user goes back from Checkout to Space.
            util.pushStateIncrement()
            displayCheckout()
          }
          if (searchParams.has("cancelled")) {
            logAmplitudeEvent("Cancelled From Stripe")
          }
        } catch (err) {
          util.logToSentry(err)
        }
      }

      // Without this, if you select a date then switch away from the browser, when you click back on the page Flatpickr opens again.
      try {
        document.getElementsByClassName("flatpickr-input")[0].addEventListener(
          "focus",
          function (e) {
            e.currentTarget.blur()
          },
          true
        )
        document.getElementById("date-select").addEventListener(
          "focus",
          function (e) {
            e.currentTarget.blur()
          },
          true
        )
      } catch (err) {
        util.logToSentry(err)
      }

      // The recurring booking Typeform redirects back to the space page with ?sb=1
      // to trigger an availability check when the page opens
      if (searchParams.has("sb") && searchParams.get("sb") === "1") {
        checkRecurringAvailability()
      }
    }

    function checkRecurringAvailability() {
      const startDateTime = DateTime.fromISO(date + "T" + startTime, {
        zone: util.TIMEZONE,
      })

      util.deleteUrlParam("sb")

      // Either _____ or 22%2F08%2F2025
      const endDateParam = searchParams.get("end_date")
      const endDateTime =
        !endDateParam || endDateParam === "_____"
          ? startDateTime.plus({ months: 4 })
          : DateTime.fromFormat(endDateParam, "dd/MM/yyyy")

      // "Monday, Tuesday, Wednesday"
      const weekdays = searchParams.get("days")
      const weekdaysArray = weekdays.split(", ")

      const termTimeOnly = searchParams.get("when") === "Term time only"

      let availableDates = []
      let unavailableDates = []
      for (const dateData of spacesJson[0].dates) {
        const dateTimeStart = DateTime.fromISO(
          dateData.start_date + "T" + startTime,
          {
            zone: util.TIMEZONE,
          }
        )
        if (dateTimeStart > endDateTime) {
          break
        }
        if (
          dateTimeStart >= startDateTime &&
          weekdaysArray.includes(dateTimeStart.toFormat("EEEE")) &&
          (!termTimeOnly || isWithinTermTime(dateTimeStart))
        ) {
          let dateTimeEnd = DateTime.fromISO(
            dateData.start_date + "T" + endTime,
            {
              zone: util.TIMEZONE,
            }
          )
          if (endTime < startTime) {
            dateTimeEnd = dateTimeEnd.plus({ days: 1 })
          }
          if (
            util.checkTimesAreAvailable(
              dateTimeStart.ts,
              dateTimeEnd.ts,
              dateData.times
            )
          ) {
            // Thu 27 Mar 2025 09:00 - 11:00
            availableDates.push(
              dateTimeStart.toFormat("EEE dd LLL yyyy HH:mm") +
                " - " +
                dateTimeEnd.toFormat("HH:mm")
            )
          } else {
            unavailableDates.push(
              dateTimeStart.toFormat("EEE dd LLL yyyy HH:mm") +
                " - " +
                dateTimeEnd.toFormat("HH:mm")
            )
          }
        }
      }

      document.getElementById("recurring-avail-times").innerText =
        startTime + " - " + endTime
      document.getElementById("recurring-avail-weekdays").innerText = weekdays
      let availSummaryHTML = ""
      if (endDateParam === "_____") {
        document.getElementById("recurring-avail-dates").innerText =
          "From " + startDateTime.toFormat("EEE dd LLL yyyy")
        availSummaryHTML =
          "From " +
          startDateTime.toFormat("dd LLL") +
          " to " +
          endDateTime.toFormat("dd LLL yyyy") +
          ", "
      } else {
        document.getElementById("recurring-avail-dates").innerText =
          startDateTime.toFormat("EEE dd LLL yyyy") +
          " - " +
          endDateTime.toFormat("EEE dd LLL yyyy")
      }
      if (termTimeOnly) {
        document.getElementById("recurring-avail-dates").innerText +=
          ", term time only"
      }

      availSummaryHTML +=
        "<strong>" +
        availableDates.length +
        (availableDates.length === 1 ? " date is" : " dates are") +
        " available</strong>. "
      if (unavailableDates.length == 0) {
        availSummaryHTML += "There are no unavailable dates."
      } else {
        availSummaryHTML +=
          " <br/><br/><p>Note that <strong>" +
          unavailableDates.length +
          (unavailableDates.length === 1 ? " date is" : " dates are") +
          " unavailable:</strong></p>"
        availSummaryHTML += "<ul class='red red-bullets'>"
        for (const date of unavailableDates) {
          availSummaryHTML += "<li>" + date + "</li>"
        }
        availSummaryHTML += "</ul>"

        if (
          unavailableDates.length /
            (availableDates.length + unavailableDates.length) >
          0.3
        ) {
          availSummaryHTML +=
            "<br/><strong>Sorry, there are too many unavailable dates to send an enquiry</strong>."
          document
            .getElementById("recurring-avail-enquiry")
            .classList.add("hide")
        } else {
          availSummaryHTML +=
            "<br/>Congratulations! This venue has enough availability for you to send an enquiry."
        }
      }
      document.getElementById("recurring-avail-summary").innerHTML =
        availSummaryHTML
      openModal("recurring-avail-modal")
    }

    function isWithinTermTime(dateTime) {
      // Hardcode the Barnet term dates for now
      const termDates = [
        { start: "2025-02-24", end: "2025-04-04" },
        { start: "2025-04-22", end: "2025-05-23" },
        { start: "2025-06-02", end: "2025-07-22" },
        { start: "2025-09-01", end: "2025-10-24" },
        { start: "2025-11-03", end: "2025-12-19" },
        { start: "2026-01-05", end: "2026-02-13" },
        { start: "2026-02-23", end: "2026-03-27" },
        { start: "2026-04-13", end: "2026-05-22" },
        { start: "2026-06-01", end: "2026-07-20" },
      ]

      for (const term of termDates) {
        if (
          dateTime >= DateTime.fromISO(term.start + "T00:00:00") &&
          dateTime <= DateTime.fromISO(term.end + "T23:59:59")
        ) {
          return true
        }
      }
      // If the date is greater than the largest term date, return true
      const lastTerm = termDates[termDates.length - 1]
      if (dateTime > DateTime.fromISO(lastTerm.end + "T23:59:59")) {
        return true
      }
      return false
    }

    function openModal(id) {
      document.getElementById("modal-container").style.display = "flex"
      document.getElementById(id).style.display = "flex"
    }

    function closeModal(id) {
      document.getElementById("modal-container").style.display = "none"
      document.getElementById(id).style.display = "none"
    }

    function moveMobileOnlySections(event) {
      let imageSlider = document.getElementById("swiper")
      let basket = document.getElementById("basket")
      if (event.matches) {
        // The window is now <= TABLET_MAX_WIDTH
        let mobileImageSliderBlock = document.getElementById(
          "mobile-image-slider-block"
        )
        mobileImageSliderBlock.appendChild(imageSlider)
        imageSlider.style.marginTop = "0px"

        let mobileBasketContainer = document.getElementById(
          "mobile-basket-container"
        )
        mobileBasketContainer.appendChild(basket)
      } else {
        // The window is now > TABLET_MAX_WIDTH
        let mainContentCol = document.getElementById("main-content-col")
        mainContentCol.insertBefore(imageSlider, mainContentCol.firstChild)

        document.getElementById("checkout-form").appendChild(basket)
      }
    }

    function flatpickrPosition() {
      // Set the left opening position of the desktop date picker. Delete the style when switching to tablet or below.
      let flatpickrTextbox =
        document.getElementsByClassName("flatpickr-input")[0]
      if (
        window.matchMedia("(max-width: " + util.TABLET_MAX_WIDTH + "px)")
          .matches
      ) {
        // width is <= TABLET_MAX_WIDTH
        try {
          flatpickrTextbox.style.removeProperty("margin-left")
        } catch (err) {}
      } else {
        // width is > TABLET_MAX_WIDTH
        let dateSelectWidth = document.getElementById("date-select").offsetWidth
        // Align the calendar with the right margin of the date-select box. Calendar width is 324px.
        flatpickrTextbox.style.marginLeft = -1 * (324 - dateSelectWidth) + "px"
      }
    }

    /* This is being done because of a Webflow limitation where it isn't possible to output a reference of a reference.
     * In this case references are Space -> Venue -> Area, and it's not possible to access the Area. The Area breadcrumb
     * is output on the venue page so we just fetch that and take from there. Yes I realise this is horrible.
     */
    async function areaBreadcrumb() {
      // Get data from the Webflow CMS venue page
      const venueUrl = document.getElementById("breadcrumb-venue-link").href
      const response = await fetch(venueUrl)
      const html = await response.text()
      const doc = new DOMParser().parseFromString(html, "text/html")
      let breadcrumbAreaLink = doc.getElementById("breadcrumb-area-link")
      if (breadcrumbAreaLink) {
        if (breadcrumbAreaLink.innerText.length > 0) {
          document.getElementById("breadcrumb-area").style.display = "flex"
          document.getElementById("breadcrumb-area-link").href =
            breadcrumbAreaLink.href
          document.getElementById("breadcrumb-area-link").innerText =
            breadcrumbAreaLink.innerText
          let spaceTypes = document.querySelectorAll("#space-types a")
          spaceTypes.forEach(function (spaceType) {
            // Replace the default location with the LA, to give SEO credit to the more specific link
            spaceType.href =
              breadcrumbAreaLink.href + "?" + spaceType.href.split("?")[1]
          })
        }
        const level = doc.getElementById("level-hidden").innerText
        let href = breadcrumbAreaLink.href
        if (level >= "3") {
          document.getElementById("breadcrumb-l2").style.display = "flex"
          href = href.substring(0, href.lastIndexOf("/"))
          document.getElementById("breadcrumb-l2-link").href = href
          document.getElementById("breadcrumb-l2-link").innerText =
            util.capitalisedNameFromSlug(href.split("/").pop())
        }
        if (level >= "2") {
          document.getElementById("breadcrumb-l1").style.display = "flex"
          href = href.substring(0, href.lastIndexOf("/"))
          document.getElementById("breadcrumb-l1-link").href = href
          document.getElementById("breadcrumb-l1-link").innerText =
            util.capitalisedNameFromSlug(href.split("/").pop())
        }
      }
    }

    function checkoutError(userError, amplitudeEvent, amplitudeEventProps) {
      if (amplitudeEvent) {
        if (amplitudeEventProps) {
          logAmplitudeEvent(amplitudeEvent, amplitudeEventProps)
        } else {
          logAmplitudeEvent(amplitudeEvent)
        }
      }
      displayCheckoutError(userError)
      if (amplitudeEvent !== "Times Not Valid At Checkout") {
        util.logToSentry("Checkout error - " + amplitudeEvent)
      }
    }

    function displayCheckoutError(message1, message2) {
      document.getElementById("unavailable-error").innerText = message1
      if (message2) {
        document.getElementById("unavailable-error-2").innerText = message2
        document.getElementById("unavailable-error-2").classList.remove("hide")
      } else {
        document.getElementById("unavailable-error-2").classList.add("hide")
      }
      let checkoutError = document.getElementById("checkout-error")
      checkoutError.style.display = "flex"
      checkoutError.scrollIntoView({
        block: "center",
        behavior: "smooth",
      })
    }

    function displayDateSelector() {
      if (
        window.getComputedStyle(document.getElementById("mobile-book-sticky"))
          .display !== "none"
      ) {
        displayMobileBookModal()
      }
      fp.open()
    }

    function displayTimeSelector() {
      if (
        window.getComputedStyle(document.getElementById("mobile-book-sticky"))
          .display !== "none"
      ) {
        displayMobileBookModal()
      }
      document.querySelector(".time-modal").style.display = "flex"
    }

    function openSuperbookerModal() {
      openModal("superbooker-modal")
      logAmplitudeEvent("Open Superbooker Modal")
      if (!date || !startTime || !endTime) {
        // The next time the user selects the date and time we will show a more visible
        // "Make a recurring booking" button, otherwise it isn't obvious what they
        // should do after selecting date and time.
        localStorage.setItem("recurringModalShownWhenTimeNotSelected", "true")
      }
    }

    function displayViewingSection() {
      util.updateUrlParam("viewing", 1, { type: "viewing" }, false)
      document.getElementById("viewing-content").classList.remove("hide")
      document.getElementById("space-content").style.display = "none"

      const bookingSectionInner = document.getElementById(
        "booking-section-inner"
      )
      document.getElementById("viewing-dates").appendChild(bookingSectionInner)
      document.getElementById("booking-section-button").classList.add("hide")
      document.getElementById("booking-section-header").classList.add("hide")
      document.getElementById("viewing-dates-proceed").classList.remove("hide")

      // Scroll to the top of the page
      window.scrollTo(0, 0)

      logAmplitudeEvent("Open Venue Viewing Section")
    }

    function closeViewingSection() {
      logAmplitudeEvent("Close Venue Viewing Section")
      history.back()
    }

    function openViewingWhenSection() {
      document.getElementById("viewing-when-section").classList.remove("hide")
      document.getElementById("viewing-times-preferred").required = true
    }

    function viewingDatesProceed() {
      if (!date) {
        fp.open()
      } else if (!startTime || !endTime) {
        displayTimeModal(true)
      } else {
        logAmplitudeEvent("Viewing Event Date Proceed")
        document.getElementById("viewing-dates").classList.add("hide")
        openViewingWhenSection()
      }
    }

    function displayEnquirySection() {
      util.updateUrlParam("enquiry", 1, { type: "enquiry" }, false)
      document.getElementById("enquiry-content").classList.remove("hide")
      document.getElementById("space-content").style.display = "none"

      const bookingSectionInner = document.getElementById(
        "booking-section-inner"
      )
      document.getElementById("enquiry-dates").appendChild(bookingSectionInner)
      document.getElementById("booking-section-button").classList.add("hide")
      document.getElementById("booking-section-header").classList.add("hide")
      document.getElementById("enquiry-dates-proceed").classList.remove("hide")

      // Scroll to the top of the page
      window.scrollTo(0, 0)

      logAmplitudeEvent("Open Enquiry Section")
    }

    function closeEnquirySection() {
      logAmplitudeEvent("Close Enquiry Section")
      history.back()
    }

    function openEnquiryMessageSection() {
      document
        .getElementById("enquiry-message-section")
        .classList.remove("hide")
      document.getElementById("enquiry-message").required = true
    }

    function enquiryDatesProceed() {
      if (!date) {
        fp.open()
      } else if (!startTime || !endTime) {
        displayTimeModal(true)
      } else {
        logAmplitudeEvent("Enquiry Event Date Proceed")
        document.getElementById("enquiry-dates").classList.add("hide")
        openEnquiryMessageSection()
      }
    }

    function hideStandardCheckoutElements() {
      document.getElementById("payment-icons").classList.add("hide")
      document.getElementById("checkout-cancel-section").classList.add("hide")
      document.getElementById("basket-price-row").classList.add("hide")
      document.getElementById("viewing-section").classList.add("hide")
      document
        .getElementById("marketing-channel-container")
        .classList.add("hide")
      document.getElementById("clean-up-section").classList.add("hide")
      document.getElementById("cleaning-checkbox").required = false
      document.getElementById("marketing-channel").required = false
      document.getElementById("extend-yes").required = false
      document.getElementById("extend-no").required = false
      document.getElementById("checkout-button").value = "Send request"
    }

    function init() {
      try {
        if (
          document.getElementsByClassName("enquiryonly-hidden")[0].innerText ===
          "true"
        ) {
          enquiryOnly = true

          let enquiryButton = document.getElementById("enquiry-only-button")
          try {
            let venueName = document
              .getElementsByClassName("venue-hidden")[0]
              .innerText.replace(/,/g, "\\,")
            util.addUrlParamToLink(enquiryButton, "venue", venueName)
          } catch (err) {}
          try {
            let spaceName = document
              .getElementsByClassName("space-hidden")[0]
              .innerText.replace(/,/g, "\\,")
            util.addUrlParamToLink(enquiryButton, "space", spaceName)
          } catch (err) {}
          enquiryButton.addEventListener("click", function (e) {
            const eventProperties = {
              "Selected Date": document.querySelector(".text-date-input-space")
                .value,
              "Start Time": startTime,
              "End Time": endTime,
            }
            if (startTimeOnNextDay) {
              eventProperties["Start Time On Next Day"] = true
            }
            if (endTimeOnNextDay) {
              eventProperties["End Time On Next Day"] = true
            }
            logAmplitudeEvent("Press Enquire Now", eventProperties)
          })
        }
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        searchParams = util.getSearchParams() // refresh search params
        if (searchParams.has("starttime") && searchParams.has("endtime")) {
          startTime =
            searchParams.get("starttime").substring(0, 2) +
            ":" +
            searchParams.get("starttime").substring(2, 4)
          endTime =
            searchParams.get("endtime").substring(0, 2) +
            ":" +
            searchParams.get("endtime").substring(2, 4)
          if (searchParams.has("starttimeonnextday")) {
            startTimeOnNextDay = true
          }
          if (
            searchParams.has("endtimeonnextday") ||
            (endTime < startTime && endTime <= "06:00")
          ) {
            endTimeOnNextDay = true
          }
        }
        if (searchParams.has("date")) {
          date = searchParams.get("date")
        }
      } catch (err) {
        util.logToSentry(err)
      }

      // I couldn't move the image slider with CSS alone so used this instead
      try {
        const mediaQueryList = window.matchMedia(
          "(max-width: " + util.TABLET_MAX_WIDTH + "px)"
        )
        try {
          mediaQueryList.addEventListener("change", moveMobileOnlySections)
        } catch (err) {
          // Safari before version 14
          mediaQueryList.addListener(moveMobileOnlySections)
        }
        // Need to call once on page load
        moveMobileOnlySections(mediaQueryList)
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.getElementById("offer-pill").classList.remove("hide")
        document.getElementById("offer-pill-mobile").classList.remove("hide")
      } catch (err) {
        util.logToSentry(err)
      }

      // replace logo with back button on mobile if referred from search
      try {
        const backButton = document.getElementById("back-button-mobile")
        const host = "www.sharesy.com"
        const searchPathname = "/hire-a-venue/"
        const referrer = new URL(document.referrer)
        if (
          getComputedStyle(backButton).display === "block" && // if block we are on mobile viewport
          referrer.host === host &&
          referrer.pathname.includes(searchPathname)
        ) {
          const logo = document.getElementById("sharesy-logo")
          const logoLink = logo.parentElement
          logoLink.replaceChild(backButton, logo)
          logoLink.href = referrer.href
          logoLink.addEventListener("click", function (event) {
            if (util.getSearchParams().has("checkout")) {
              // If user has made it to checkout, we just want to go back to space page, not the search page
              event.preventDefault()
              window.history.back()
              logAmplitudeEvent("Checkout Back Button Clicked")
            } else {
              logAmplitudeEvent("Back Button Clicked", {
                "Search URL": logoLink.href,
              })
            }
          })
          backButton.style.height = "30px"
          backButton.style.width = "30px"
          backButton.style.marginLeft = "-8px"
        }
      } catch (err) {
        // don't log to sentry if the referrer was not valid; the error is expected
        if (document.referrer.length) {
          util.logToSentry(err)
        }
      }

      try {
        document.getElementById("time-modal-footer").classList.add("hide")
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        let imageSlides = document.getElementsByClassName("swiper-slide")
        let invalidSlides = []
        for (const i of imageSlides) {
          // If image isn't set the src will be the space page URL. Don't do a
          // direct comparison to window.location.href as the current space URL
          // may have e.g. a hash param
          if (
            i.childNodes[0].src.startsWith(
              window.location.origin + window.location.pathname
            )
          ) {
            invalidSlides.push(i)
          }
        }
        // Remove empty images from Slider
        for (const i of invalidSlides) {
          i.classList.remove("swiper-slide")
        }
        swiper = new Swiper(".swiper", {
          pagination: {
            el: ".swiper-pagination",
          },
          navigation: {
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
          },
          keyboard: {
            enabled: true,
          },
          loop: false,
        })
      } catch (err) {
        util.logToSentry(err)
      }

      // Recurring booking event listeners
      try {
        document
          .getElementById("text-link-recurring")
          .addEventListener("click", (e) => {
            openSuperbookerModal()
          })
        document
          .getElementById("text-link-recurring-mobile")
          .addEventListener("click", (e) => {
            openSuperbookerModal()
          })
        document
          .getElementById("recurring-button")
          .addEventListener("click", (e) => {
            openSuperbookerModal()
          })
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("superbooker-modal-close")
          .addEventListener("click", (e) => {
            closeModal("superbooker-modal")
            logAmplitudeEvent("Close Superbooker Modal")
          })
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("superbooker-choose-time")
          .addEventListener("click", (e) => {
            closeModal("superbooker-modal")
            if (date) {
              displayTimeSelector()
            } else {
              displayDateSelector()
            }
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("request-viewing")
          .addEventListener("click", (e) => {
            displayViewingSection()
          })
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("viewing-content-close")
          .addEventListener("click", (e) => {
            closeViewingSection()
          })
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("viewing-dates-proceed")
          .addEventListener("click", (e) => {
            viewingDatesProceed()
          })
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        if (document.querySelector('input[name="viewing-wait"]')) {
          document
            .querySelectorAll('input[name="viewing-wait"]')
            .forEach((elem) => {
              elem.addEventListener("change", function (event) {
                logAmplitudeEvent("Viewing Wait Until Checkout", {
                  Wait: event.target.value,
                })
                if (event.target.value === "yes") {
                  closeViewingSection()
                } else {
                  document
                    .getElementById("viewing-wait-section")
                    .classList.add("hide")
                  document
                    .getElementById("viewing-dates")
                    .classList.remove("hide")
                }
              })
            })
        }
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("viewing-proceed")
          .addEventListener("click", async (e) => {
            // dont submit
            e.preventDefault()
            const viewingForm = document.getElementById("venue-viewing-form")
            if (viewingForm.checkValidity()) {
              logAmplitudeEvent("Viewing Proceed Button Clicked")
              await displayCheckout()

              // Hide parts of checkout that aren't relevant to viewing. Do this
              // after displayCheckout otherwise it will unhide some elements
              // such as payment-icons
              hideStandardCheckoutElements()
              checkoutMode = "viewing"
            } else {
              viewingForm.reportValidity()
            }
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("message-venue")
          .addEventListener("click", (e) => {
            displayEnquirySection()
          })
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("enquiry-content-close")
          .addEventListener("click", (e) => {
            closeEnquirySection()
          })
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("enquiry-dates-proceed")
          .addEventListener("click", (e) => {
            enquiryDatesProceed()
          })
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("enquiry-proceed")
          .addEventListener("click", async (e) => {
            // dont submit
            e.preventDefault()
            const enquiryForm = document.getElementById("enquiry-form")
            if (enquiryForm.checkValidity()) {
              logAmplitudeEvent("Enquiry Proceed Button Clicked")
              await displayCheckout()
              // Hide parts of checkout that aren't relevant to enquiries. Do this
              // after displayCheckout otherwise it will unhide some elements
              // such as payment-icons
              hideStandardCheckoutElements()
              checkoutMode = "enquiry"
            } else {
              enquiryForm.reportValidity()
            }
          })
      } catch (err) {
        util.logToSentry(err)
      }

      // Close modals when click outside
      try {
        document
          .getElementById("modal-container")
          .addEventListener("click", (e) => {
            if (e.target.id === "modal-container") {
              if (
                window.getComputedStyle(
                  document.getElementById("recurring-avail-modal")
                ).display === "flex" ||
                window.getComputedStyle(
                  document.getElementById("viewing-success-modal")
                ).display === "flex"
              ) {
                return
              }
              closeModal("superbooker-modal")
              document.getElementById("all-amenities-modal").style.display =
                "none"
              logAmplitudeEvent("Close Modals - Click Outside")
            }
          })
      } catch (err) {
        util.logToSentry(err)
      }

      // Recurring booking Typeform
      try {
        document
          .getElementById("superbooker-availability-form")
          .addEventListener("click", (e) => {
            let spaceName = ""
            let spaceSlug = null
            let hourlyPrice = null
            try {
              spaceName = document
                .getElementById("space-h1")
                .innerText.replace(" Hire", "")
              spaceSlug =
                document.documentElement.getAttribute("data-wf-item-slug")
              hourlyPrice = document
                .getElementById("price-block-price")
                .innerText.replace("£", "")
            } catch (err) {
              util.logToSentry(err)
            }
            // Create popup with latest data
            const superbookerAvailabilityPopup = window.tf.createPopup(
              "h8lP6MmR",
              {
                hidden: {
                  space: spaceName,
                  space_slug: spaceSlug,
                  start_date: date,
                  start_time: startTime,
                  end_time: endTime,
                  starttime: startTime.replace(":", ""),
                  endtime: endTime.replace(":", ""),
                  hourly_price: hourlyPrice,
                },
                onReady: () => {
                  SharesyWebflow.logAmplitudeEvent(
                    "Space Superbooker Typeform Ready",
                    { Availability: false }
                  )
                },
                onQuestionChanged: ({ formId, ref }) => {
                  SharesyWebflow.logAmplitudeEvent(
                    "Space Superbooker Typeform Question Change",
                    {
                      Availability: false,
                      question: ref,
                      "Form ID": formId,
                    }
                  )
                },
                onSubmit: ({ formId, responseId }) => {
                  SharesyWebflow.logAmplitudeEvent(
                    "Space Superbooker Typeform Submit",
                    {
                      Availability: false,
                      "Form ID": formId,
                      "Response ID": responseId,
                    }
                  )
                },
                onClose: () => {
                  SharesyWebflow.logAmplitudeEvent(
                    "Space Superbooker Typeform Close",
                    { Availability: false }
                  )
                },
                onEndingButtonClick: ({ formId, ref }) => {
                  SharesyWebflow.logAmplitudeEvent(
                    "Space Superbooker Typeform Ended",
                    {
                      Availability: false,
                      "Form ID": formId,
                      "Redirect screen": ref,
                    }
                  )
                },
              }
            )
            // Open the popup
            superbookerAvailabilityPopup.open()
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("recurring-avail-enquiry")
          .addEventListener("click", (e) => {
            let spaceName = ""
            let spaceSlug = null
            let hourlyPrice = null
            try {
              spaceName = document
                .getElementById("space-h1")
                .innerText.replace(" Hire", "")
              spaceSlug =
                document.documentElement.getAttribute("data-wf-item-slug")
              hourlyPrice = document
                .getElementById("price-block-price")
                .innerText.replace("£", "")
            } catch (err) {
              util.logToSentry(err)
            }
            // Create popup with latest data
            const superbookerAvailabilityPopup = window.tf.createPopup(
              "h8lP6MmR",
              {
                hidden: {
                  space: spaceName,
                  space_slug: spaceSlug,
                  start_date: date,
                  start_time: startTime,
                  end_time: endTime,
                  starttime: startTime.replace(":", ""),
                  endtime: endTime.replace(":", ""),
                  hourly_price: hourlyPrice,
                  frequency_param: searchParams.get("frequency"),
                  days_param: searchParams.get("days"),
                  end_date_param: searchParams.get("end_date"),
                  when_param: searchParams.get("when"),
                },
                onReady: () => {
                  SharesyWebflow.logAmplitudeEvent(
                    "Space Superbooker Typeform Ready",
                    { Availability: true }
                  )
                },
                onQuestionChanged: ({ formId, ref }) => {
                  SharesyWebflow.logAmplitudeEvent(
                    "Space Superbooker Typeform Question Change",
                    {
                      Availability: true,
                      question: ref,
                      "Form ID": formId,
                    }
                  )
                },
                onSubmit: ({ formId, responseId }) => {
                  SharesyWebflow.logAmplitudeEvent(
                    "Space Superbooker Typeform Submit",
                    {
                      Availability: true,
                      "Form ID": formId,
                      "Response ID": responseId,
                    }
                  )
                },
                onClose: () => {
                  SharesyWebflow.logAmplitudeEvent(
                    "Space Superbooker Typeform Close",
                    { Availability: true }
                  )
                },
                onEndingButtonClick: ({ formId, ref }) => {
                  SharesyWebflow.logAmplitudeEvent(
                    "Space Superbooker Typeform Ended",
                    {
                      Availability: true,
                      "Form ID": formId,
                      "Redirect screen": ref,
                    }
                  )
                },
              }
            )
            // Open the popup
            superbookerAvailabilityPopup.open()
            closeModal("recurring-avail-modal")
          })
      } catch (err) {
        util.logToSentry(err)
      }

      // See all amenities event listeners
      try {
        document
          .getElementById("all-amenities")
          .addEventListener("click", (e) => {
            openModal("all-amenities-modal")
            logAmplitudeEvent("Open All Amenities Modal")
          })
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("all-amenities-modal-close")
          .addEventListener("click", (e) => {
            closeModal("all-amenities-modal")
            logAmplitudeEvent("Close All Amenities Modal")
          })
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("recurring-avail-cancel")
          .addEventListener("click", (e) => {
            closeModal("recurring-avail-modal")
            logAmplitudeEvent("Close Recurring Availability Modal")
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        let mobileSeeAvailButton = document.getElementById("mobile-book-button")
        mobileSeeAvailButton.addEventListener("click", function (e) {
          logAmplitudeEvent("Open Availability Modal")
        })
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("venue-link")
          .addEventListener("click", function (e) {
            logAmplitudeEvent("Click Venue Link")
          })
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("read-full-terms")
          .addEventListener("click", function (e) {
            logAmplitudeEvent("Click Full Terms Link")
          })
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("breadcrumb-home")
          .addEventListener("click", function (e) {
            logAmplitudeEvent("Click Breadcrumb Home")
          })
        document
          .getElementById("breadcrumb-l1-link")
          .addEventListener("click", function (e) {
            logAmplitudeEvent("Click Breadcrumb L1 (City)")
          })
        document
          .getElementById("breadcrumb-l2-link")
          .addEventListener("click", function (e) {
            logAmplitudeEvent("Click Breadcrumb L2 (Sub City)")
          })
        document
          .getElementById("breadcrumb-area-link")
          .addEventListener("click", function (e) {
            logAmplitudeEvent("Click Breadcrumb Area")
          })
        document
          .getElementById("breadcrumb-venue-link")
          .addEventListener("click", function (e) {
            logAmplitudeEvent("Click Breadcrumb Venue")
          })
      } catch (err) {
        util.logToSentry(err)
      }

      // Amplitude Select Space events
      try {
        const spaceCards = document.getElementsByClassName("space-card")
        for (const sc of spaceCards) {
          sc.addEventListener("click", function (e) {
            // Use currentTarget rather than target to get the <a> tag
            const eventProperties = {
              "New Space": e.currentTarget
                .querySelector(".space-name")
                .innerText.replace("'", ""),
              "New Venue": e.currentTarget
                .querySelector(".venue-name")
                .innerText.replace("'", ""),
              "New Venue + Space": e.currentTarget
                .querySelector(".name-hidden")
                .innerText.replace("'", ""),
              "New Capacity":
                e.currentTarget.querySelector(".capacity-hidden").innerText,
              "New Price": e.currentTarget.querySelector(
                ".price-hidden > .price-inner"
              ).innerText,
            }
            logAmplitudeEvent("Select Space", eventProperties)
          })
        }
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        getSpaceAvailability()
      } catch (error) {
        util.logToSentry(err)
      }

      // Hide sections if the space is cancelled or delisted.
      try {
        let availabilityState = document.getElementsByClassName(
          "availability-hidden"
        )[0].innerText
        if (
          availabilityState === "delisted" ||
          availabilityState === "cancelled"
        ) {
          document.getElementById("mobile-book-sticky").style.display = "none"
          document.getElementById("booking-widget-col").style.display = "none"
          document.getElementById(
            "after-booking-section-mobile"
          ).style.display = "none"
          document.getElementById("request-viewing").style.display = "none"
          document
            .getElementById("not-available-error")
            .classList.remove("hide")
        }
      } catch (err) {
        util.logToSentry(err)
      }

      // Set review stars
      try {
        const reviews = document.getElementsByClassName("review")
        const starFull = document.getElementById("star-full")
        const starEmpty = document.getElementById("star-empty")
        const starHalf = document.getElementById("star-half")
        let reviewTotal = 0
        // Add stars for individual reviews
        for (const r of reviews) {
          let stars = r.querySelector("#stars")
          let reviewScore = parseInt(r.querySelector("#review-score").innerText)
          reviewTotal += reviewScore
          for (let i = 1; i <= 5; i++) {
            if (i <= reviewScore) {
              stars.appendChild(starFull.cloneNode(true))
            } else {
              stars.appendChild(starEmpty.cloneNode(true))
            }
          }
        }
        // Display review total
        if (reviews.length > 0) {
          const starsTotal = document.getElementById("stars-total")
          const ratingTotal = document.getElementById("rating-total")
          let reviewAverage = reviewTotal / reviews.length
          ratingTotal.innerText = reviewAverage.toFixed(1)
          for (let i = 1; i <= 5; i++) {
            if (reviewAverage - i >= -0.25) {
              starsTotal.appendChild(starFull.cloneNode(true))
            } else if (reviewAverage - i >= -0.75) {
              starsTotal.appendChild(starHalf.cloneNode(true))
            } else {
              starsTotal.appendChild(starEmpty.cloneNode(true))
            }
          }
          starsTotal.style.display = "inline-flex"
        }
      } catch (err) {
        util.logToSentry(err)
      }
      try {
        document
          .getElementById("stars-total")
          .addEventListener("click", function (e) {
            logAmplitudeEvent("Click Review Stars")
          })
      } catch (err) {
        util.logToSentry(err)
      }

      // Add an area breadcrumb link if appropriate
      try {
        areaBreadcrumb()
      } catch (err) {
        util.logToSentry(err)
      }

      // Add venue viewing Youtube video if necessary
      try {
        youtubeId = document.getElementById("viewing-youtube-hidden").innerText
        if (youtubeId.length > 0) {
          // Unhide the YouTube section before running the script in case it causes an error:
          // https://stackoverflow.com/questions/50857499/youtube-api-error-failed-to-execute-postmessage-on-domwindow
          document.getElementById("youtube-section").style.display = "block"
          let tag = document.createElement("script")
          tag.src = "https://www.youtube.com/iframe_api"
          let firstScriptTag = document.getElementsByTagName("script")[0]
          firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)
          document.getElementById("video-tour-link").classList.remove("hide")
          logAmplitudeEvent("Venue viewing video exists")
          swiper.appendSlide([
            '<div class="swiper-slide">' +
              document
                .getElementById("hero-image")
                .outerHTML.replace('id="hero-image"', "") +
              '<div class="virtual-tour-image-wrapper"><div class="virtual-tour-text">View virtual tour</div><a href="#youtube-header" class="ga-event" data-event-category="Webflow" data-event-action="View Virtual Tour Image Link"><img src="https://assets-global.website-files.com/5f5f7fb0593ba84e85cb2ac4/62d18af38f7b537f77f47864_yt_icon_mono_light.png" loading="lazy" class="virtual-tour-image"></a></div></div>',
          ])
        }
      } catch (err) {
        util.logToSentry(err)
      }

      // Add event listeners to venue viewing survey
      try {
        document
          .getElementById("survey-q1-more")
          .addEventListener("click", function (e) {
            document.getElementById("survey-question-1").classList.add("hide")
            document
              .getElementById("survey-question-2")
              .classList.remove("hide")
            document.getElementById("survey-textarea").focus()
            logAmplitudeEvent("Venue Viewing Survey Response", {
              "Question Num": "1",
              Question:
                "After watching this tour, are you more or less likely to book?",
              Response: "More",
            })
          })
        document
          .getElementById("survey-q1-less")
          .addEventListener("click", function (e) {
            document.getElementById("survey-question-1").classList.add("hide")
            document
              .getElementById("survey-question-2")
              .classList.remove("hide")
            document.getElementById("survey-textarea").focus()
            logAmplitudeEvent("Venue Viewing Survey Response", {
              "Question Num": "1",
              Question:
                "After watching this tour, are you more or less likely to book?",
              Response: "Less",
            })
          })
        document
          .getElementById("survey-q2-ok")
          .addEventListener("click", function (e) {
            document.getElementById("survey-question-2").classList.add("hide")
            document.getElementById("survey-end").classList.remove("hide")
            logAmplitudeEvent("Venue Viewing Survey Response", {
              "Question Num": "2",
              Question:
                "Is there anything different you wanted to see in the tour?",
              Response: document.getElementById("survey-textarea").value,
            })
          })
      } catch (err) {
        util.logToSentry(err)
      }

      // If no date is selected, when user clicks 'Choose a date' in cancellation section open date picker
      try {
        if (
          document.getElementById("custom-cancellation-policy").innerText ===
          "false"
        ) {
          document
            .getElementById("cancellation-policy-choose-date")
            .addEventListener("click", function (e) {
              displayDateSelector()
            })
        }
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document.querySelectorAll('input[name="extend"]').forEach((elem) => {
          elem.addEventListener("change", function (event) {
            if (event.target.value === "yes") {
              add30MinsExtension()
            } else {
              remove30MinsExtension()
            }
            document
              .getElementById("cleaning-checkbox-field")
              .classList.remove("hide")
            logAmplitudeEvent("Extend 30 Mins Changed", {
              "Input Value": event.target.value,
            })
          })
        })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("continue-button")
          .addEventListener("click", function (e) {
            selectedAddons = []
            document
              .getElementById("add-on-items")
              .querySelectorAll("input[type=checkbox]:checked")
              .forEach(function (checkedAddon) {
                let addonItem = checkedAddon.parentElement.parentElement
                let addonTextDivs = addonItem
                  .getElementsByClassName("checkbox-item-text")[0]
                  .querySelectorAll("div")
                selectedAddons.push({
                  id: checkedAddon.getAttribute("addon_id"),
                  name: addonTextDivs[0].innerText,
                  price: addonTextDivs[1].dataset.price,
                  is_hourly_price: addonTextDivs[1].dataset.is_hourly_price,
                })
              })
            displayCheckoutAddons()
            logAmplitudeEvent("Addons Press Continue")
            util.updateUrlParam("checkout", 1, { type: "checkout" }, false)
            util.updateUrlParam(
              "addons",
              encodeURIComponent(JSON.stringify(selectedAddons)),
              false,
              false
            )
            displayCheckout()
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        document
          .getElementById("checkout-button")
          .addEventListener("click", async function (e) {
            e.preventDefault()
            let checkoutForm = document.getElementById("checkout-form")
            // Since addons can be specified via query param, check the price matches, otherwise user could set their own price.
            try {
              for (const addon of selectedAddons) {
                let match = availableAddons.filter(
                  (obj) => obj.id.toString() === addon.id
                )[0]
                let matchPrice = parseFloat(match.price)
                if (match.is_hourly_price) {
                  matchPrice = matchPrice * checkoutNumHours
                }
                matchPrice = matchPrice.toFixed(2)
                if (addon.price !== matchPrice) {
                  util.logToSentry("Add-on Price Mismatch")
                  checkoutError(
                    "The price of the add-ons you selected has changed. Please make your selection again. If you continue to see this message email help@sharesy.com and we'll manually book you in.",
                    "Add-on Price Mismatch",
                    {
                      "System Add-on Price": matchPrice,
                      "selectedAddons Price": addon.price,
                    }
                  )
                  return
                }
              }
            } catch (err) {
              util.logToSentry(err)
              checkoutError(
                "An error has occurred while trying to proceed. Please click to refresh, and if the problem continues email help@sharesy.com and we'll manually book you in.",
                "Unexpected Add-on Error"
              )
            }
            if (
              validatePhone(true) &&
              validateEmail(true) &&
              checkoutForm.checkValidity()
            ) {
              // Only block form submission if the form is valid (so HTML5 validation errors will still be shown)

              let timeAndPriceText = document.getElementById(
                "basket-price-detail"
              ).innerText
              // Check if extra 30 mins is selected
              if (document.getElementById("extend-yes").checked) {
                let [endHours, endMinutes] = endTime.split(":")
                endHours = parseInt(endHours)
                endMinutes = parseInt(endMinutes) + 30
                if (endMinutes >= 60) {
                  endMinutes -= 60
                  endHours = endHours + 1
                }
                if (endHours === 24) {
                  endHours = 0
                }
                let newTime =
                  endHours.toString().padStart(2, "0") +
                  ":" +
                  endMinutes.toString().padStart(2, "0")
                if (newTime < endTime) {
                  endTimeOnNextDay = true
                }
                endTime = newTime

                checkoutTotalPrice += parseFloat(priceOfExtra30Mins)

                let newHours = parseFloat(checkoutNumHours) + 0.5
                let hourlyPrice = util.roundToMaxDp(
                  checkoutTotalPrice / newHours,
                  2
                )
                let hourText = "hours"
                if (newHours === 1) {
                  hourText = "hour"
                }
                checkoutNumHours = util.roundToMaxDp(newHours, 1)
                timeAndPriceText =
                  "£" + hourlyPrice + " x " + checkoutNumHours + " " + hourText
              }

              if (
                !util.checkTimesAreAvailable(
                  getStartDateTime().ts,
                  getEndDateTime().ts,
                  availableTimes
                )
              ) {
                checkoutError(
                  "The times you have selected for hire are no longer available. Please go back and select different times.",
                  "Times Not Valid At Checkout"
                )
                return
              }
              let checkoutButton = document.getElementById("checkout-button")
              let checkoutButtonText = checkoutButton.value
              window.requestAnimationFrame(() => {
                document.getElementById("checkout-error").style.display = "none"
                checkoutButton.value = "Please wait..."
                checkoutButton.disabled = true
              })

              let phoneNum = intlTelInputValue?.getNumber()
              if (!phoneNum) {
                phoneNum = document.getElementById("phone").value
              }
              let jsonBody = {
                hours: checkoutNumHours,
                totalPrice: checkoutTotalPrice,
                startDate: date,
                startTime: startTime,
                endTime: endTime,
                endTimeOnNextDay: endTimeOnNextDay,
                slug: slug,
                venueName:
                  document.getElementsByClassName("venue-hidden")[0].innerText,
                spaceName:
                  document.getElementsByClassName("space-hidden")[0].innerText,
                email: document.getElementById("email").value,
                firstName: document.getElementById("first-name").value,
                lastName: document.getElementById("last-name").value,
                phone: phoneNum,
                imageUrl: document.getElementById("basket-image").currentSrc,
                currentUrl: window.location.href,
                friendlyDate: document.getElementById("basket-date").innerText,
                friendlyTimes: startTime + " - " + endTime,
                basketPriceDetail: timeAndPriceText,
                basketPriceTotal: "£" + checkoutTotalPrice.toFixed(2),
                addons: selectedAddons,
                usingNewSystem: true,
              }
              if (document.getElementById("extend-yes").checked) {
                jsonBody["extend30Mins"] = "True"
              }
              let eventType = document.getElementById("event-type")
              // if eventType has an option selected
              if (eventType.selectedIndex > 0) {
                jsonBody["question_" + getQuestionIdFromElement(eventType)] =
                  eventType.options[eventType.selectedIndex].value
              }
              let eventTypeOther = document.getElementById("event-type-other")
              if (eventTypeOther.required) {
                if (eventTypeOther.value !== "") {
                  jsonBody[
                    "question_" + getQuestionIdFromElement(eventTypeOther)
                  ] = eventTypeOther.value
                } else {
                  util.logToSentry(
                    "Other event type required but not provided at checkout"
                  )
                }
              }
              let numGuests = document.getElementById("num-guests")
              let numGuestsValue = numGuests.value
              if (numGuests.value === "") {
                numGuestsValue =
                  "Not sure yet, less than " +
                  document.querySelector(".capacity-hidden").innerText
              }
              if (numGuestsValue !== "") {
                jsonBody["question_" + getQuestionIdFromElement(numGuests)] =
                  numGuestsValue
              } else {
                util.logToSentry("Num guests not selected at checkout")
              }
              let marketingChannel =
                document.getElementById("marketing-channel")
              if (marketingChannel.selectedIndex > 0) {
                jsonBody[
                  "question_" + getQuestionIdFromElement(marketingChannel)
                ] =
                  marketingChannel.options[marketingChannel.selectedIndex].value
              }
              let furnitureEquipment = document.getElementById(
                "furniture-equipment"
              )
              if (furnitureEquipment.value !== "") {
                jsonBody[
                  "question_" + getQuestionIdFromElement(furnitureEquipment)
                ] = furnitureEquipment.value
              }
              let anythingElse = document.getElementById("anything-else")
              if (anythingElse.value !== "") {
                jsonBody["question_" + getQuestionIdFromElement(anythingElse)] =
                  anythingElse.value
              }
              let alcoholYes = document.getElementById("alcohol-yes")
              if (alcoholYes.required) {
                let alcoholSelected = document.querySelector(
                  'input[name="alcohol"]:checked'
                )
                if (alcoholSelected) {
                  jsonBody["question_" + getQuestionIdFromElement(alcoholYes)] =
                    alcoholSelected.value
                } else {
                  util.logToSentry("Alcohol yes or no not selected at checkout")
                }
              }
              let visitSelected = document.querySelector(
                'input[name="visit"]:checked'
              )
              if (visitSelected) {
                jsonBody["venueVisit"] = visitSelected.value
              } else {
                visitSelected = document.querySelector(
                  'input[name="venue-visit"]:checked'
                )
                if (visitSelected) {
                  jsonBody["venueVisit"] = visitSelected.value
                }
              }
              let cleaningCheckbox =
                document.getElementById("cleaning-checkbox")
              if (cleaningCheckbox.checked) {
                jsonBody[
                  "question_" + getQuestionIdFromElement(cleaningCheckbox)
                ] = "True"
              } else if (checkoutMode == "booking") {
                util.logToSentry("Cleaning disclaimer not accepted at checkout")
              }
              jsonBody["payLater"] = false
              jsonBody["stripeCustomText"] = cancellationMessage
              let paySelection = document.querySelector(
                'input[name="pay"]:checked'
              )
              if (paySelection?.value == "later") {
                jsonBody["payLater"] = true
                jsonBody["stripeCustomText"] = payLaterMessage
              }
              if (searchParams.has("booker")) {
                jsonBody["encryptedEmailForPricing"] =
                  searchParams.get("booker")
              }
              // Add the dynamic questions
              document
                .querySelectorAll("[question_id]")
                .forEach((questionElem) => {
                  let questionId = parseInt(
                    questionElem.getAttribute("question_id")
                  )
                  if (!standardCheckoutQuestionIds.includes(questionId)) {
                    if (questionElem.type === "checkbox") {
                      if (questionElem.checked) {
                        jsonBody["question_" + questionId] = "True"
                      }
                    } else if (questionElem.type === "select-one") {
                      jsonBody["question_" + questionId] =
                        questionElem.options[questionElem.selectedIndex].value
                    } else {
                      jsonBody["question_" + questionId] = questionElem.value
                    }
                  }
                })
              try {
                if (amplitude.getInstance()) {
                  jsonBody["amplitude_device_id"] =
                    amplitude.getInstance().options.deviceId
                }
              } catch (err) {}

              if (checkoutMode == "booking") {
                try {
                  const data = await util.api
                    .post("checkout-session", {
                      json: jsonBody,
                      retry: { limit: 4 },
                    })
                    .json()
                  if (data.url) {
                    logAmplitudeEvent("Redirecting to Stripe Checkout")
                    // Change the button text back before redirecting
                    checkoutButton.value = checkoutButtonText
                    checkoutButton.disabled = false
                    window.location.href = data.url
                  } else if (data.error) {
                    if (data.error === "Time is not available") {
                      logAmplitudeEvent("Checkout Attempt For Unavailable Time")
                      checkoutButton.value = checkoutButtonText
                      checkoutButton.disabled = false
                      displayCheckoutError(
                        "The space is no longer available for hire from " +
                          startTime +
                          " - " +
                          endTime +
                          " on " +
                          document.getElementById("basket-date").innerText +
                          ".",
                        "Please go back and select a different time."
                      )
                    } else if (data.error === "Addon is not available") {
                      logAmplitudeEvent(
                        "Checkout Attempt For Unavailable Addon"
                      )
                      checkoutButton.value = checkoutButtonText
                      checkoutButton.disabled = false
                      displayCheckoutError(
                        "One of the 'extras' you added is no longer available for hire from " +
                          startTime +
                          " - " +
                          endTime +
                          " on " +
                          document.getElementById("basket-date").innerText +
                          ".",
                        "Please go back and change your selection."
                      )
                    } else if (data.error === "Invalid email") {
                      logAmplitudeEvent("Checkout Attempt With Invalid Email")
                      checkoutButton.value = checkoutButtonText
                      checkoutButton.disabled = false
                      displayCheckoutError(
                        "The email address you entered is not valid."
                      )
                    } else {
                      checkoutError(
                        "An error has occurred while trying to proceed. Please refresh and try again, and if the problem continues email help@sharesy.com and we'll manually book you in.",
                        "Unknown Error Creating Checkout Session",
                        { Error: data.error }
                      )
                    }
                  } else {
                    checkoutError(
                      "An error has occurred while trying to proceed. Please refresh and try again, and if the problem continues email help@sharesy.com and we'll manually book you in.",
                      "Unknown Error Creating Checkout Session"
                    )
                  }
                } catch (error) {
                  checkoutError(
                    "An error has occurred while trying to proceed. Please refresh and try again, and if the problem continues email help@sharesy.com and we'll manually book you in.",
                    "Unknown Error Calling checkout-session"
                  )
                }
              } else if (checkoutMode == "viewing") {
                checkoutButton.value = checkoutButtonText
                jsonBody["type"] = "viewing"
                jsonBody["initialMessage"] = document.getElementById(
                  "viewing-times-preferred"
                ).value
                const data = await util.api
                  .post("create-enquiry", {
                    json: jsonBody,
                    retry: { limit: 4 },
                  })
                  .json()
                logAmplitudeEvent("Viewing Enquiry Created")
                openModal("viewing-success-modal")
              } else if (checkoutMode == "enquiry") {
                checkoutButton.value = checkoutButtonText
                jsonBody["type"] = "enquiry"
                jsonBody["initialMessage"] =
                  document.getElementById("enquiry-message").value
                const data = await util.api
                  .post("create-enquiry", {
                    json: jsonBody,
                    retry: { limit: 4 },
                  })
                  .json()
                logAmplitudeEvent("Enquiry Created")
                openModal("viewing-success-modal")
              }
            } else {
              checkoutForm.reportValidity()
              logAmplitudeEvent("Checkout Form Invalid")
            }
          })
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        window.addEventListener("popstate", function (e) {
          // Check if there are any previous pushStates that we need to 'pop'.
          // If there are just reload the page as we don't have functions
          // to easily remove checkout or addons (if we do then we can copy
          // how search.js works).
          if (util.popStateCheck()) {
            // If there are hash params the page wasn't reloading, so remove them
            window.location.href = window.location.href.split("#")[0]
            // Hide in case user is pressing back after seeing error message
            document.getElementById("checkout-error").style.display = "none"
          }
        })
      } catch (err) {
        util.logToSentry(err)
      }

      // This is necessary because HTML5 validation doesn't work properly for iOS Safari and Chrome for select and checkboxes
      try {
        let platform =
          navigator?.platform || navigator?.userAgentData?.platform || "unknown"
        if (platform.indexOf("iPhone") > -1 || platform.indexOf("iPad") > -1) {
          let inputs = document
            .getElementById("checkout-form")
            .querySelectorAll(
              'select, input[type="checkbox"], input[type="radio"]'
            )
          inputs.forEach((i) => {
            i.addEventListener("focus", function (e) {
              e.target.scrollIntoViewIfNeeded()
            })
          })
        }
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        if (navigator.share) {
          let shareSection = document.getElementById("share")
          shareSection.classList.remove("hide")
          shareSection.addEventListener("click", function (e) {
            let title = "Hire " + document.getElementById("space-h1").innerText
            navigator.share({
              title: title,
              url: window.location.href,
            })
            logAmplitudeEvent("Space Page Mobile Share")
          })
        }
      } catch (err) {
        util.logToSentry(err)
      }

      try {
        cancelTippy = tippy(
          document.getElementById("free-cancellation-hover"),
          {
            content: "",
          }
        )
      } catch (err) {
        util.logToSentry(err)
      }
    }

    return {
      init: init,
      logAmplitudeEvent: logAmplitudeEvent,
    }
  })()
}
